import { useEffect, useState, useRef, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Chip,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  Switch,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import axios from "axios";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import ClinicalEditor from "../../../../components/ClinicalEditor/ClinicalEditor";
import PaginatedClinicalDocument from "../../../../components/ClinicalEditor/PaginatedDocument";

import { validateDischargePayload } from "../../utils/validateDischargePayload";
import { fieldKind, toInputValue, fromInputValue } from "../../utils/summaryFieldValue";
import { fetchMarRows, buildMarAnnexureHtml } from "../../utils/marAnnexure";
import { mapCarePlanMedication } from "../../utils/carePlanMedications";
import { computeArticulationDiff } from "../../utils/articulationDiff";
import { unmetCompletenessItems } from "../../utils/dischargeCompleteness";
import { hydrateTemplate } from "../../utils/templateHydrator";
import { formatClinicalDate } from "../../utils/jsonToHtmlParser";
import html2pdf from "html2pdf.js";
import DischargeSummaryService from "../../../../services/DischargeSummaryService";
import TemplateSelectionStep from "./TemplateSelectionStep";
import CopyMedicationsDialog from "./CopyMedicationsDialog";
import ArticulationDiffDialog from "./ArticulationDiffDialog";
import { toast } from "react-toastify";
// Used by buildPdfDefinition (the Step-1 pdfmake preview) only.
import { hospitalLogoBase64 } from "./hospitalLogo";
import {
  stampLetterheadV5,
  V5,
  resolveLogo,
  resolveFacilityDetails,
} from "../../../../utils/reportLetterhead";
import { BrandingContext } from "../../../../BrandingProvider";
import { usePermissions } from "../../../login/PermissionContext";
import { fetchDischargeProcess, updateSummaryStatus, MANNER_OF_DEATH_LABELS } from "../../api/dischargeProcessApi";
// nurseApi is self-contained (own base URL, own tenant headers), so this adds
// no coupling beyond the call itself.
import { getVitals, listOf } from "../../../nurse-dashboard/api/nurseApi";
import { formatIstDateTime } from "../../../../utils/istDateTime";

pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs || pdfFonts;


/* ─── Constants ─────────────────────────────────────────────────────── */

const STEP_LABELS = [
  "Select Template",
  "Clinical Summary",
  "Edit Document",
  "Preview & Submit",
];

// summary_status (DischargeProcess, Task C6) display labels/colors — distinct
// from STAGE_LABELS (DischargeProcess.stage, the ward/billing/etc pipeline).
const SUMMARY_STATUS_LABELS = {
  draft: "Draft",
  pending_approval: "Pending Approval",
  approved: "Approved",
  rejected: "Rejected",
};

const SUMMARY_STATUS_CHIP_COLOR = {
  draft: "default",
  pending_approval: "warning",
  approved: "success",
  rejected: "error",
};

/* ─── Helpers ───────────────────────────────────────────────────────── */

const asArray = (value) => (Array.isArray(value) ? value : []);

const getNonEmptyItems = (arr) => {
  return asArray(arr).filter(item => item !== null && item !== undefined && item !== "");
};

const getNonEmptyMedications = (arr) => {
  return asArray(arr).filter(item =>
    item && (item.name || item.drug_name || item.dose || item.frequency || item.duration || item.purpose || item.start || item.period)
  );
};

/** True when an object carries the discharge-summary fields we edit/render. */
const looksLikeSummary = (o) =>
  o && typeof o === "object" && (o.header || o.history || o.advices);

/**
 * Normalize a discharge-summary API response into the flat summary shape the
 * editor / validator / PDF builder expect (top-level header, history, advices,
 * treatment_given, …).
 *
 * Backends may wrap the summary one or more levels deep under `payload`
 * (e.g. { data: { payload: { payload: {…summary}, treatment_given: {…} } } }),
 * and the structured medications array sometimes lives on a wrapper level while
 * the inner summary holds only prose. This unwraps a `{ data }` envelope, walks
 * down the `payload` chain to the object that actually carries the summary
 * fields, then recovers the medications array (and admission_id) from the nearest
 * wrapper when the core lacks them. An already-flat payload (old shape / saved
 * drafts) has its summary fields at the top level, so it is returned unchanged.
 */
const normalizeSummaryShape = (input) => {
  if (looksLikeSummary(input) || !input || typeof input !== "object") {
    return input;
  }

  // Unwrap a top-level API envelope ({ status, data, … }) before walking.
  let root = input;
  if (!looksLikeSummary(root) && root.data && typeof root.data === "object") {
    root = root.data;
  }

  // Walk down the `payload` chain, remembering each level, until we reach the
  // object that carries the actual summary fields.
  const levels = [];
  let node = root;
  let guard = 0;
  while (node && typeof node === "object" && guard < 6) {
    levels.push(node);
    if (looksLikeSummary(node)) break;
    if (!node.payload || typeof node.payload !== "object") break;
    node = node.payload;
    guard += 1;
  }

  if (!looksLikeSummary(node)) return input; // nothing summary-shaped — leave as-is

  const summary = JSON.parse(JSON.stringify(node));

  // Recover the structured medications array from a wrapper level when the core
  // summary doesn't carry one (the inner summary often holds only prose).
  if (getNonEmptyMedications(summary?.treatment_given?.medications_administered).length === 0) {
    for (const lvl of levels) {
      const meds = lvl?.treatment_given?.medications_administered;
      if (getNonEmptyMedications(meds).length > 0) {
        if (!summary.treatment_given || typeof summary.treatment_given !== "object") {
          summary.treatment_given = {};
        }
        summary.treatment_given.medications_administered = meds;
        break;
      }
    }
  }

  // Carry admission_id up from a wrapper level if the core lacks it.
  if (summary.admission_id == null) {
    for (const lvl of levels) {
      if (lvl?.admission_id != null) {
        summary.admission_id = lvl.admission_id;
        break;
      }
    }
  }

  return summary;
};

const formatDisplayValuePdf = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (typeof value === "object") {
    const keys = Object.keys(value);
    return keys.length ? JSON.stringify(value) : "-";
  }
  return String(value);
};

const toLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

/* ─── Sub-components ────────────────────────────────────────────────── */

const SectionCard = ({ title, children }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      p: 1.5,
      mb: 1.5,
      backgroundColor: "#fff",
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/**
 * One editable field of the clinical summary.
 *
 * Values are classified before rendering and coerced back to their original
 * shape on edit — see utils/summaryFieldValue.js. Previously an array was
 * joined to a string and an object JSON.stringify'd, and whatever came back out
 * of the text box was written over the original, so editing a structured field
 * destroyed it.
 */
const ValueRow = ({ label, value, onChange, kind: kindProp, disabled = false }) => {
  const kind = kindProp || fieldKind(value);
  const displayVal = toInputValue(value, kind);

  if (kind === "boolean") {
    return (
      <Box sx={{ mb: 0.75 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(value)}
              disabled={disabled}
              onChange={(e) => onChange && onChange(e.target.checked)}
            />
          }
          label={label}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 0.75 }}>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={displayVal}
        // A structured value cannot survive a round trip through a text box, so
        // it is shown but not editable here — the document editor is where it
        // gets changed.
        disabled={disabled || kind === "locked"}
        helperText={kind === "locked" ? "Structured value — edit it in the document" : undefined}
        placeholder={value === null || value === undefined ? "Not specified" : ""}
        onChange={(e) => onChange && onChange(fromInputValue(e.target.value, kind))}
        InputLabelProps={{ shrink: true }}
      />
    </Box>
  );
};

/**
 * Editable list of free-text entries with per-row delete (✕) and an Add button.
 * Replaces the old phantom-blank rendering so removed rows actually disappear and
 * an empty list stays empty.
 */
const EditableStringList = ({
  items,
  itemLabel,
  onItemChange,
  onAdd,
  onRemove,
  addLabel,
  disabled = false,
}) => {
  const list = asArray(items);
  return (
    <>
      {list.map((item, index) => (
        <Box key={`${itemLabel}-${index}`} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
          <TextField
            fullWidth
            size="small"
            label={`${itemLabel} ${index + 1}`}
            value={item || ""}
            placeholder="Not specified"
            disabled={disabled}
            onChange={(e) => onItemChange(index, e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => onRemove(index)}
            aria-label={`Remove ${itemLabel} ${index + 1}`}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={onAdd} disabled={disabled} sx={{ mt: 0.5 }}>
        {addLabel || `Add ${itemLabel}`}
      </Button>
    </>
  );
};

/* ─── Death (MCCD) hydration for a death-summary template ───────────────── */

// The `death.*` data a death-summary template hydrates from. Placeholders (via
// keyMap aliases in templateHydrator.js): {{cause_of_death}} (a due to b due to
// c), {{underlying_cause}}, {{manner_of_death}}, {{certifying_doctor}},
// {{certifying_doctor_regno}}, {{time_of_death}}, {{cod_1a}}/{{cod_1a_interval}}
// … {{cod_1c}}, {{cod_part2}}, {{mccd_number}}.
export function buildDeathHydration(dp) {
  const p1 = Array.isArray(dp?.mccd_part1) ? dp.mccd_part1 : [];
  const p2 = Array.isArray(dp?.mccd_part2) ? dp.mccd_part2 : [];
  const underlying = [...p1].reverse().find((r) => r.condition)?.condition || "";
  return {
    cause_text: p1.map((r) => r.condition).filter(Boolean).join(" due to "),
    underlying,
    manner: MANNER_OF_DEATH_LABELS[dp?.manner_of_death] || dp?.manner_of_death || "",
    certifying_doctor: dp?.certifying_doctor_name || "",
    certifying_doctor_regno: dp?.certifying_doctor_registration_no || "",
    certifying_doctor_quals: dp?.certifying_doctor_qualifications || "",
    time_of_death: dp?.death_datetime || "",
    cod_1a: p1[0]?.condition || "",
    cod_1a_interval: p1[0]?.interval || "",
    cod_1b: p1[1]?.condition || "",
    cod_1b_interval: p1[1]?.interval || "",
    cod_1c: p1[2]?.condition || "",
    cod_1c_interval: p1[2]?.interval || "",
    part2: p2.map((r) => r.condition).filter(Boolean).join("; "),
    mccd_number: dp?.mccd_number || "",
  };
}

/* ─── PDF definition builder ────────────────────────────────────────── */

export const buildPdfDefinition = (data) => {
  if (!data) return null;

  return {
    pageSize: 'A4',
    pageMargins: [40, 90, 40, 100],
    header: (currentPage, pageCount) => {
      return {
        margin: [40, 20, 40, 0],
        columns: [
          {
            stack: [
              { image: hospitalLogoBase64, width: 100, margin: [0, 0, 0, 5] },
              { text: data.hospital_details?.address || 'Hospital Address', style: 'hospitalAddressHeader' },
              { text: `Contact: ${data.hospital_details?.contact || 'N/A'}`, style: 'hospitalAddressHeader' },
            ],
            width: '*'
          },
          {
            stack: [
              { text: `Patient ID: ${data.header?.patient_id || '-'}`, bold: true, fontSize: 10 },
              { text: `Page ${currentPage} of ${pageCount}`, fontSize: 9, color: 'gray', margin: [0, 2, 0, 0] }
            ],
            alignment: 'right',
            width: 150
          }
        ],
        canvas: [{ type: 'line', x1: 40, y1: 75, x2: 555, y2: 75, lineWidth: 1, lineColor: '#cccccc' }]
      };
    },
    footer: (currentPage, pageCount) => {
      // Show signature lines only on the last page
      if (currentPage === pageCount) {
        return {
          margin: [40, 10, 40, 0],
          columns: [
            {
              stack: [
                { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 150, y2: 0, lineWidth: 1 }], margin: [0, 0, 0, 5] },
                { text: 'Prepared By', fontSize: 10 },
                { text: data.prepared_by?.name || '-', bold: true, fontSize: 10 },
              ],
              alignment: 'center',
            },
            {
              stack: [
                { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 150, y2: 0, lineWidth: 1 }], margin: [0, 0, 0, 5] },
                { text: 'Checked By', fontSize: 10 },
                { text: data.checked_by?.name || '-', bold: true, fontSize: 10 },
              ],
              alignment: 'center',
            },
          ],
        };
      }
      return {};
    },
    content: [
      { text: (data.document_type || 'Discharge Summary').toUpperCase(), style: 'docTitle' },

      {
        table: {
          widths: ['*', '*'],
          body: [
            [
              { text: [{ text: 'Patient ID: ', bold: true }, data.header?.patient_id || '-'] },
              { text: [{ text: 'Consultant: ', bold: true }, data.header?.consultant_incharge || '-'] },
            ],
            [
              { text: [{ text: 'Patient Name: ', bold: true }, data.header?.name || '-'] },
              { text: [{ text: 'Admission Date: ', bold: true }, formatClinicalDate(data.header?.date_of_admission)] },
            ],
            [
              { text: [{ text: 'Age/Gender: ', bold: true }, `${data.header?.age || '-'} / ${data.header?.gender || '-'}`] },
              { text: [{ text: 'Discharge Date: ', bold: true }, formatClinicalDate(data.header?.date_of_discharge)] },
            ],
            [
              { text: [{ text: 'Bed No: ', bold: true }, data.header?.bed_no || '-'] },
              { text: [{ text: 'Discharge Type: ', bold: true }, data.header?.discharge_type || '-'] },
            ],
            [
              { text: [{ text: 'Category: ', bold: true }, data.header?.category || '-'] },
              { text: '' },
            ],
          ],
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 15],
      },

      // Cause of Death — shown only for a deceased patient (NABH AAC.14g: the
      // death summary must carry the cause of death). Sourced from the MCCD on the
      // DischargeProcess, injected as data._death at the call site.
      ...(data._death ? [
        { text: 'CAUSE OF DEATH', style: 'sectionTitle' },
        {
          margin: [10, 0, 0, 6],
          stack: [
            ...((data._death.part1 || []).map((row, i) => ({
              text: [
                { text: `${['(a)', 'due to (b)', 'due to (c)'][i] || ''} `, bold: true },
                row.condition || '-',
                row.interval ? { text: `  (${row.interval})`, color: 'gray' } : '',
              ],
            }))),
            ...((data._death.part2 || []).length
              ? [{ text: [{ text: 'Contributing: ', bold: true }, (data._death.part2 || []).map((r) => r.condition).filter(Boolean).join('; ')] }]
              : []),
          ],
        },
        {
          columns: [
            { text: [{ text: 'Manner: ', bold: true }, data._death.manner || '-'] },
            { text: [{ text: 'Time of Death: ', bold: true }, formatClinicalDate(data._death.timeOfDeath)] },
          ],
          margin: [0, 0, 0, 3],
        },
        { text: [{ text: 'Certified by: ', bold: true }, data._death.certifyingDoctorName || '-'], margin: [0, 0, 0, 15] },
      ] : []),

      { text: 'FINAL DIAGNOSIS', style: 'sectionTitle' },
      getNonEmptyItems(data.final_diagnosis).length > 0 
        ? { ul: getNonEmptyItems(data.final_diagnosis), margin: [10, 0, 0, 15] }
        : { text: 'None reported', margin: [0, 0, 0, 15], italics: true, color: 'gray' },

      { text: 'INVESTIGATIONS', style: 'sectionTitle' },
      getNonEmptyItems(data.investigations).length > 0 
        ? { ul: getNonEmptyItems(data.investigations), margin: [10, 0, 0, 15] }
        : { text: 'None reported', margin: [0, 0, 0, 15], italics: true, color: 'gray' },

      { text: 'DISCHARGE DETAILS', style: 'sectionTitle' },
      {
        columns: [
          { text: [{ text: 'Condition: ', bold: true }, data.header?.discharge_details?.condition_at_discharge || '-'] },
          { text: [{ text: 'Remarks: ', bold: true }, data.header?.discharge_details?.remarks || '-'] },
        ],
        margin: [0, 0, 0, 5],
      },
      {
        columns: [
          { text: [{ text: 'Mobility: ', bold: true }, data.header?.discharge_details?.mobility_status || '-'] },
          { text: [{ text: 'Follow-up Req.: ', bold: true }, formatDisplayValuePdf(data.header?.discharge_details?.follow_up_required)] },
        ],
        margin: [0, 0, 0, 10],
      },
      { text: 'Vital Signs at Discharge:', bold: true, margin: [0, 0, 0, 5] },
      {
        columns: ['bp', 'pulse', 'temperature', 'spo2'].map((v) => ({
          text: [{ text: `${v.toUpperCase()}: `, bold: true }, data.header?.discharge_details?.vital_signs?.[v] || '-'],
        })),
        margin: [0, 0, 0, 15],
      },

      { text: 'HISTORY', style: 'sectionTitle' },
      { text: [{ text: 'Presenting Illness: ', bold: true }, data.history?.history_of_present_illness || '-'], margin: [0, 0, 0, 5] },
      { text: [{ text: 'Presenting Complaints: ', bold: true }, getNonEmptyItems(data.history?.presenting_complaints).join(", ") || '-'], margin: [0, 0, 0, 5] },
      { text: [{ text: 'Allergies: ', bold: true }, formatDisplayValuePdf(data.history?.allergies)], margin: [0, 0, 0, 5] },
      { text: [{ text: 'Family History: ', bold: true }, formatDisplayValuePdf(data.history?.family_history)], margin: [0, 0, 0, 5] },
      { text: [{ text: 'Past Medical History: ', bold: true }, formatDisplayValuePdf(data.history?.past_medical_history)], margin: [0, 0, 0, 5] },
      { text: [{ text: 'Past Surgical History: ', bold: true }, formatDisplayValuePdf(data.history?.past_surgical_history)], margin: [0, 0, 0, 5] },
      {
        text: [
          { text: 'Personal History: ', bold: true },
          `Diet: ${data.history?.personal_history?.diet || '-'}, Smoking: ${data.history?.personal_history?.smoking || '-'}, Alcohol: ${data.history?.personal_history?.alcohol || '-'}`
        ],
        margin: [0, 0, 0, 15]
      },

      { text: 'TREATMENT GIVEN', style: 'sectionTitle' },
      { text: 'Procedures', bold: true, margin: [0, 0, 0, 2] },
      getNonEmptyItems(data.treatment_given?.procedures).length > 0 
        ? { ul: getNonEmptyItems(data.treatment_given?.procedures), margin: [10, 0, 0, 8] }
        : { text: 'None reported', margin: [0, 0, 0, 8], italics: true, color: 'gray' },
        
      { text: 'Medications Administered', bold: true, margin: [0, 0, 0, 5] },
      {
        table: {
          headerRows: 1,
          widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto'],
          body: [
            [
              { text: 'Name', bold: true, fillColor: '#f2f2f2' },
              { text: 'Dose', bold: true, fillColor: '#f2f2f2' },
              { text: 'Freq.', bold: true, fillColor: '#f2f2f2' },
              { text: 'Start', bold: true, fillColor: '#f2f2f2' },
              { text: 'Period', bold: true, fillColor: '#f2f2f2' },
              { text: 'Unit', bold: true, fillColor: '#f2f2f2' },
            ],
            ...(getNonEmptyMedications(data.treatment_given?.medications_administered).length > 0
              ? getNonEmptyMedications(data.treatment_given?.medications_administered).map(m => [
                m.name || m.drug_name || '-', m.dose || '-', m.frequency || '-', formatClinicalDate(m.start), m.period || '-', m.periodUnit || '-'
              ])
              : [[{ text: 'No medications administered', colSpan: 6, alignment: 'center', italics: true, color: 'gray' }, {}, {}, {}, {}, {}]])
          ]
        },
        margin: [0, 0, 0, 10],
        layout: 'lightHorizontalLines'
      },
      
      { text: 'Supportive Care', bold: true, margin: [0, 0, 0, 2] },
      getNonEmptyItems(data.treatment_given?.supportive_care).length > 0
        ? { ul: getNonEmptyItems(data.treatment_given?.supportive_care), margin: [10, 0, 0, 15] }
        : { text: 'None reported', margin: [0, 0, 0, 15], italics: true, color: 'gray' },

      { text: 'DISCHARGE ADVICES', style: 'sectionTitle' },
      { text: 'Medications To Continue', bold: true, margin: [0, 0, 0, 5] },
      {
        table: {
          headerRows: 1,
          widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto'],
          body: [
            [
              { text: 'Name', bold: true, fillColor: '#f2f2f2' },
              { text: 'Dose', bold: true, fillColor: '#f2f2f2' },
              { text: 'Freq.', bold: true, fillColor: '#f2f2f2' },
              { text: 'Start', bold: true, fillColor: '#f2f2f2' },
              { text: 'Period', bold: true, fillColor: '#f2f2f2' },
              { text: 'Unit', bold: true, fillColor: '#f2f2f2' },
            ],
            ...(getNonEmptyMedications(data.advices?.medications_to_continue).length > 0
              ? getNonEmptyMedications(data.advices?.medications_to_continue).map(m => [
                m.name || m.drug_name || '-', m.dose || '-', m.frequency || '-', formatClinicalDate(m.start), m.period || '-', m.periodUnit || '-'
              ])
              : [[{ text: 'No medications to continue', colSpan: 6, alignment: 'center', italics: true, color: 'gray' }, {}, {}, {}, {}, {}]])
          ]
        },
        margin: [0, 0, 0, 10],
        layout: 'lightHorizontalLines'
      },
      { text: [{ text: 'Diet: ', bold: true }, `${data.advices?.diet?.type || '-'} - ${getNonEmptyItems(data.advices?.diet?.recommendations).join(", ") || '-'}`], margin: [0, 0, 0, 5] },
      { text: [{ text: 'Activity Restrictions: ', bold: true }, getNonEmptyItems(data.advices?.activity_restrictions).join(", ") || '-'], margin: [0, 0, 0, 5] },
      { text: [{ text: 'Warning Signs: ', bold: true }, getNonEmptyItems(data.advices?.warning_signs).join(", ") || '-'], margin: [0, 0, 0, 5] },
      {
        text: [
          { text: 'Follow-up: ', bold: true },
          `${data.advices?.follow_up?.department || '-'} in ${data.advices?.follow_up?.after_days || '-'} (Purpose: ${data.advices?.follow_up?.purpose || '-'})`
        ],
        margin: [0, 0, 0, 15]
      },

      { text: 'REFERRAL DETAILS', style: 'sectionTitle' },
      {
        columns: [
          { text: [{ text: 'Referred By: ', bold: true }, data.referral_details?.referred_by || '-'] },
          { text: [{ text: 'Date: ', bold: true }, formatClinicalDate(data.referral_details?.referral_date)] },
        ],
        margin: [0, 0, 0, 5],
      },
      { text: [{ text: 'Reason: ', bold: true }, data.referral_details?.reason_for_referral || '-'], margin: [0, 0, 0, 15] },
    ],
    styles: {
      hospitalNameHeader: {
        fontSize: 16,
        bold: true,
        color: '#1976d2',
        margin: [0, 0, 0, 2],
      },
      hospitalAddressHeader: {
        fontSize: 9,
        color: 'gray',
      },
      docTitle: {
        fontSize: 14,
        bold: true,
        alignment: 'center',
        margin: [0, 0, 0, 15],
      },
      sectionTitle: {
        fontSize: 12,
        bold: true,
        color: '#1976d2',
        margin: [0, 10, 0, 5],
      },
      tableHeader: {
        bold: true,
        fontSize: 10,
        color: 'black'
      }
    },
    defaultStyle: {
      fontSize: 10,
      lineHeight: 1.2,
    },
  };
};

/* ─── Inline PDF Preview ────────────────────────────────────────────── */

const DischargeSummaryPreview = ({ data }) => {
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!data) return;

    setLoading(true);
    const timer = setTimeout(() => {
      try {
        const docDef = buildPdfDefinition(data);
        const pdfDocGenerator = pdfMake.createPdf(docDef);
        pdfDocGenerator.getBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const newId = Date.now();
          setFrames((prev) => [...prev, { id: newId, url: url, loaded: false }]);
          setLoading(false);

          // Fallback: PDF iframes often don't fire onLoad. Manually trigger load completion after 150ms.
          setTimeout(() => handleLoad(newId), 150);
        });
      } catch (err) {
        console.error("Failed to generate PDF:", err);
        setLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [data]);

  const handleLoad = (id) => {
    setFrames((prev) => {
      const updated = prev.map((f) => (f.id === id ? { ...f, loaded: true } : f));
      const latestLoadedIndex = updated.map((f) => f.loaded).lastIndexOf(true);

      if (latestLoadedIndex !== -1) {
        const toKeep = updated.slice(latestLoadedIndex);
        const toDrop = updated.slice(0, latestLoadedIndex);

        toDrop.forEach((f) => setTimeout(() => URL.revokeObjectURL(f.url), 2000));
        return toKeep;
      }
      return updated;
    });
  };

  useEffect(() => {
    return () => {
      frames.forEach((f) => URL.revokeObjectURL(f.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return <Box sx={{ p: 3, textAlign: 'center' }}>No Data Available</Box>;

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative', display: 'flex', flexDirection: 'column', backgroundColor: '#e0e0e0' }}>
      {loading && (
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 9999 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {frames.length === 0 ? (
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Typography>Generating Document Preview...</Typography>
        </Box>
      ) : (
        frames.map((frame, index) => (
          <iframe
            key={frame.id}
            src={`${frame.url}#toolbar=0&navpanes=0&scrollbar=0`}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              zIndex: index,
              opacity: frame.loaded ? 1 : 0,
              transition: 'opacity 0.2s ease-in-out',
            }}
            title={`Discharge Summary Preview ${frame.id}`}
          />
        ))
      )}
    </Box>
  );
};

// Client-side, UX-only check for "is the current user the admitting
// doctor". The backend is the real authorization boundary for the
// summary_status "approved" transition — this only decides whether to show
// a heads-up; fail-open (allow) whenever admittingDoctorId isn't supplied,
// mirroring CancelDischargeModal.js's identical helper (Task C14).
function isAdmittingDoctor(admittingDoctorId) {
  if (!admittingDoctorId) return true;
  const doctorId = sessionStorage.getItem("doctor_id");
  if (!doctorId) return true;
  return String(doctorId) === String(admittingDoctorId);
}

/* ═══════════════════════════════════════════════════════════════════════
   MAIN MODAL
   ═══════════════════════════════════════════════════════════════════════ */

const DischargeSummaryModal = ({
  open,
  onClose,
  admissionId,
  patientName,
  patientAge,
  patientGender,
  patientBedNo,
  authHeaders,
  admittingDoctorId,
}) => {
  // Facility branding + details for the letterhead. resolveLogo prefers the
  // logo uploaded through Branding Management; resolveFacilityDetails reads the
  // real address off get_logged_in_user_info/.
  const brandingData = useContext(BrandingContext);
  const { userInfo } = usePermissions();

  // ── Step management ──────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTemplateObj, setSelectedTemplateObj] = useState(null);
  // ── Data state ───────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [summaryData, setSummaryData] = useState(null);

  // ── Editor state ─────────────────────────────────────────────────────
  const editorRef = useRef(null);
  const [htmlContent, setHtmlContent] = useState("");

  // ── Print Mode state ─────────────────────────────────────────────────
  const [printMode, setPrintMode] = useState("blank"); // 'blank' or 'letterhead'

  // ── Draft / save state ───────────────────────────────────────────────
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [draftCheckData, setDraftCheckData] = useState(null);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [checkingDraft, setCheckingDraft] = useState(false);

  // ── Document lifecycle refs ──────────────────────────────────────────
  // True once htmlContent has been populated for the editor (via compile or
  // resume). From that point HTML is the source of truth, so JSON→HTML
  // regeneration is gated behind this flag and never clobbers editor edits.
  const htmlInitializedRef = useRef(false);
  // True when the editor has unsaved changes since the last (auto)save — used to
  // drive debounced autosave and a final flush on close.
  const dirtyRef = useRef(false);
  // Medication names merged in from care plans this session, so the Step-1 form
  // can caption which rows the doctor did not type. Display-only and
  // deliberately NOT part of summaryData — it must never reach final_json or the
  // articulation payload.
  const carePlanMedNamesRef = useRef(new Set());
  // Confirm dialog shown when re-compiling from the form would overwrite edits.
  const [showRecompileConfirm, setShowRecompileConfirm] = useState(false);

  // ── Discharge-process approval status (Task C15) ───────────────────────
  // undefined = loading, null = no active DischargeProcess for this
  // admission, object = the fetched DischargeProcess (id + summary_status
  // used here). Sentinel convention mirrors PatientDetail.js's
  // loadDischargeProcess.
  const [dischargeProcess, setDischargeProcess] = useState(undefined);
  const [updatingSummaryStatus, setUpdatingSummaryStatus] = useState(false);

  // True once a finalized PDF exists in S3 for this admission, read from
  // get_discharge_pdf_url/'s `found` flag. This is the one approval
  // precondition that cannot be faked client-side: the document is there or it
  // is not. Approve used to be live from Step 0, with nothing to approve.
  const [hasFinalizedDocument, setHasFinalizedDocument] = useState(false);
  // Withdrawing an approval so the document can be edited again.
  const [reopening, setReopening] = useState(false);
  // Finalize is irreversible — S3 upload, FINALIZED record, print — and was a
  // single unconfirmed click.
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  // Take-home medication picker, replacing the blanket auto-copy.
  const [copyMedsOpen, setCopyMedsOpen] = useState(false);
  // What a resume restored. Resuming used to drop the doctor straight into the
  // editor under a stepper claiming steps 0-1 were complete, with no indication
  // of what state anything was in.
  const [resumeNotice, setResumeNotice] = useState("");
  // Articulation result awaiting the doctor's accept/reject.
  // { base, enriched, changes } — null when nothing is pending.
  const [pendingArticulation, setPendingArticulation] = useState(null);
  const [articulating, setArticulating] = useState(false);
  // Pending template selection awaiting confirmation, because applying it
  // regenerates summaryData from the care plans and destroys Step-1 edits.
  const [pendingTemplate, setPendingTemplate] = useState(null);
  // Highlight values hydrateTemplate filled in, so the doctor can tell their
  // own words from the system's.
  const [showProvenance, setShowProvenance] = useState(false);
  // The MAR annexure, as HTML. Deliberately NOT part of summaryData or
  // htmlContent: it is appended at render time only, so it can never reach
  // final_json, save_discharge_summary or the articulation payload. Same
  // containment as carePlanMedNamesRef above — a nurse-signed dose record must
  // not become doctor-editable prose.
  const [marAnnexureHtml, setMarAnnexureHtml] = useState("");



  // ── Reset state and check draft when modal opens ───────────────────────
  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setSelectedTemplateObj(null);
      setErrorMessage("");
      setSummaryData(null);
      setLoading(false);
      setHtmlContent("");
      setLastSavedAt(null);
      setSavingDraft(false);
      setSubmitting(false);
      setDraftCheckData(null);
      setShowDraftPrompt(false);
      setCheckingDraft(false);
      setShowRecompileConfirm(false);
      setDischargeProcess(undefined);
      setHasFinalizedDocument(false);
      setReopening(false);
      setShowFinalizeConfirm(false);
      setCopyMedsOpen(false);
      setResumeNotice("");
      setPendingArticulation(null);
      setArticulating(false);
      setPendingTemplate(null);
      setShowProvenance(false);
      setMarAnnexureHtml("");
      htmlInitializedRef.current = false;
      dirtyRef.current = false;
      carePlanMedNamesRef.current = new Set();
    } else {
      checkExistingDraft();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Load the active DischargeProcess for this admission (approval badge
  // / actions) whenever the modal opens or the admission changes ─────────
  const loadDischargeProcess = () => {
    if (!admissionId) {
      setDischargeProcess(null);
      return;
    }
    setDischargeProcess(undefined);
    fetchDischargeProcess(admissionId)
      .then((res) => setDischargeProcess(res?.data || null))
      .catch(() => setDischargeProcess(null));
  };

  useEffect(() => {
    if (open) loadDischargeProcess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, admissionId]);

  // Never blocks: a failed fetch yields no annexure, and the summary must still
  // be finalizable.
  useEffect(() => {
    if (!open || !admissionId) return;
    let cancelled = false;
    fetchMarRows(admissionId, authHeaders)
      .then((rows) => {
        if (!cancelled) setMarAnnexureHtml(buildMarAnnexureHtml(rows));
      })
      .catch((err) => {
        console.warn("[DischargeSummary] MAR annexure unavailable:", err?.message);
        if (!cancelled) setMarAnnexureHtml("");
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, admissionId]);

  // Does a finalized document already exist? `found: false` is an ordinary
  // answer (nobody has finalized yet), not an error.
  useEffect(() => {
    if (!open || !admissionId) return;
    let cancelled = false;
    DischargeSummaryService.getDischargePdfUrl(admissionId, authHeaders)
      .then((res) => {
        if (!cancelled) setHasFinalizedDocument(res?.found === true);
      })
      .catch(() => {
        if (!cancelled) setHasFinalizedDocument(false);
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, admissionId]);

  // Submit-for-approval (draft -> pending_approval, any authenticated user)
  // and approve/reject (pending_approval -> approved/rejected) all move the
  // same summary_status field via the same endpoint — one handler, target
  // status is the only thing that differs. Applies the response's `data`
  // directly on success (cheaper than a re-fetch, per the brief).
  const handleUpdateSummaryStatus = async (targetStatus) => {
    if (!dischargeProcess?.id) {
      toast.error("Missing discharge process — cannot update approval status.");
      return;
    }
    setUpdatingSummaryStatus(true);
    try {
      const res = await updateSummaryStatus(dischargeProcess.id, targetStatus);
      if (res?.status === "Success") {
        toast.success(res.message || "Discharge summary status updated.");
        setDischargeProcess(res.data);
      } else {
        toast.error(res?.message || "Could not update discharge summary status.");
      }
    } catch (err) {
      console.error(
        "Failed to update discharge summary status:",
        err?.response?.data || err?.message
      );
      toast.error(
        err?.response?.data?.message || "Could not update discharge summary status."
      );
    } finally {
      setUpdatingSummaryStatus(false);
    }
  };

  const selectedTemplateId = () =>
    selectedTemplateObj
      ? selectedTemplateObj.template_id ||
        selectedTemplateObj.id ||
        selectedTemplateObj.templateId
      : undefined;

  const allowedToApprove = isAdmittingDoctor(admittingDoctorId);

  // Submitting for approval needs something saved to approve; approving needs a
  // finalized document that the approver has actually looked at.
  const hasSavedDraft = Boolean(lastSavedAt || draftCheckData);
  const approveBlockedReason = !hasFinalizedDocument
    ? "Finalize the summary before approving it."
    : currentStep !== 3
    ? "Open Preview & Submit and review the document before approving."
    : "";

  // An approved summary is a signed clinical document. Editing it has to mean
  // withdrawing the approval first, explicitly.
  const isApprovedLocked = dischargeProcess?.summary_status === "approved";

  // Once the document has been built, the form is the RECORD of what it was
  // built from, not a live surface. It used to stay editable and keep saving
  // while changing nothing about the document — the source of truth had moved
  // to the HTML, and only the recompile dialog ever hinted at that.
  const formLocked = htmlInitializedRef.current && currentStep === 1;

  // Deliberately NOT optimistic: updateSummaryStatus is documented as forward-
  // only (draft -> pending_approval -> approved/rejected), so the backend may
  // refuse this. If it does, the summary stays locked — which is the correct
  // outcome for an approved medico-legal document, not a bug to work around.
  const handleReopenForEditing = async () => {
    if (!dischargeProcess?.id) return;
    setReopening(true);
    try {
      const res = await updateSummaryStatus(dischargeProcess.id, "draft");
      if (res?.status === "Success") {
        toast.success(res.message || "Summary reopened for editing.");
        setDischargeProcess(res.data);
      } else {
        toast.error(res?.message || "This summary could not be reopened for editing.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "This summary could not be reopened for editing."
      );
    } finally {
      setReopening(false);
    }
  };

  const checkExistingDraft = async () => {
    try {
      setCheckingDraft(true);
      const res = await DischargeSummaryService.getDischargeSummary(admissionId, authHeaders);

      // Tolerate both flat and { data: {…} }-wrapped envelopes, plus snake/camel key variants.
      const body =
        res && typeof res.data === "object" && res.data !== null
          ? { ...res, ...res.data }
          : res || {};

      // final_json may come back as a JSON string — parse it so downstream consumers get an object.
      let finalJson = body.final_json || body.finalJson || null;
      if (typeof finalJson === "string") {
        try {
          finalJson = JSON.parse(finalJson);
        } catch {
          /* leave as the raw string; normalizeSummaryShape will ignore it */
        }
      }
      // HTML may arrive top-level OR embedded inside final_json (we now save it in
      // both places). Resolve from either so a draft saved under either shape resumes.
      const embeddedHtml =
        finalJson && typeof finalJson === "object"
          ? finalJson.html_content || finalJson.htmlContent || ""
          : "";
      const htmlContent = body.html_content || body.htmlContent || embeddedHtml || "";

      // Treat a draft as present whenever the backend returns saved content, even if it
      // doesn't send an explicit `found` flag (the save endpoint omits one too).
      const hasContent = Boolean(finalJson || htmlContent);
      const found = body.found === true || body.exists === true || hasContent;

      if (found && hasContent) {
        setDraftCheckData({ ...body, final_json: finalJson, html_content: htmlContent });
        setShowDraftPrompt(true);
      }
    } catch (err) {
      console.error("Failed to check existing summary:", err?.message);
    } finally {
      setCheckingDraft(false);
    }
  };

  const loadDraft = async (draftData) => {
    if (!draftData || (!draftData.final_json && !draftData.html_content)) return;

    let summary = draftData.final_json ? normalizeSummaryShape(draftData.final_json) : null;
    // We embed the edited HTML inside final_json on save; strip it back out of the
    // structured summary so it can't pollute the Step-1 form or the next
    // validate_discharge_payload (LLM) pass on recompile.
    const savedTemplateId =
      summary && typeof summary === "object" ? summary.__template_id : null;
    if (summary && typeof summary === "object" && !Array.isArray(summary)) {
      const {
        html_content: _omitHtml,
        htmlContent: _omitHtmlCamel,
        __template_id: _omitTemplateId,
        ...rest
      } = summary;
      summary = rest;
    }
    if (summary) setSummaryData(summary);

    // Resolve the authoritative editor HTML. Once a doctor edits in the rich-text
    // editor, those edits live ONLY in the saved HTML — `final_json` is a stale
    // seed — so we must restore that HTML rather than rebuild from JSON (which is
    // exactly how earlier edits got silently discarded on resume). The edited HTML
    // is persisted separately and read back via `get_edited_discharge_summary`.
    let editedHtml = "";
    try {
      setCheckingDraft(true);
      const res = await DischargeSummaryService.getEditedDischargeSummary(
        admissionId,
        authHeaders
      );
      // Endpoint returns raw HTML; tolerate JSON-wrapped + nested variants too.
      editedHtml =
        (typeof res === "string"
          ? res
          : res?.html_content ||
            res?.html ||
            res?.edited_html ||
            res?.data?.html_content ||
            res?.data?.html ||
            "") || "";
      editedHtml = editedHtml.trim();
    } catch (err) {
      console.warn("[DischargeSummary] Failed to fetch edited draft HTML:", err?.message);
    } finally {
      setCheckingDraft(false);
    }

    // Preference order: saved edited HTML → html_content from the get response →
    // (last resort) rebuild from the JSON seed.
    const resolvedHtml =
      editedHtml ||
      draftData.html_content ||
      (summary ? hydrateTemplate("", summary) : "");
    setHtmlContent(resolvedHtml);
    htmlInitializedRef.current = true;
    dirtyRef.current = false;

    // Re-resolve the template this draft was built from, so "Rebuild from Form"
    // reproduces the original layout instead of falling back to the generic
    // builder (hydrateTemplate("") does exactly that, silently). Not fatal if it
    // fails — the rebuild action is disabled without a template.
    let restoredTemplate = null;
    if (savedTemplateId) {
      try {
        const listRes = await DischargeSummaryService.listTemplates(authHeaders);
        const list =
          listRes?.templates || listRes?.data?.templates || listRes?.data || [];
        restoredTemplate =
          (Array.isArray(list) ? list : []).find(
            (t) =>
              String(t.template_id || t.id || t.templateId) === String(savedTemplateId)
          ) || null;
        if (restoredTemplate) setSelectedTemplateObj(restoredTemplate);
      } catch (err) {
        console.warn(
          "[DischargeSummary] Could not restore the draft's template:",
          err?.message
        );
      }
    }

    setResumeNotice(
      `Resumed the saved draft. Template: ${
        restoredTemplate
          ? restoredTemplate.template_name ||
            restoredTemplate.name ||
            restoredTemplate.title ||
            `#${savedTemplateId}`
          : "not recorded — rebuilding from the form is disabled"
      }.`
    );

    // Directly move to the editor
    setCurrentStep(2);
  };

  const handleGenerateNew = () => {
    setShowDraftPrompt(false);
    setDraftCheckData(null);
    setCurrentStep(0);
  };

  const handleContinueWithDraft = async () => {
    setShowDraftPrompt(false);
    await loadDraft(draftCheckData);
  };

  // ────────────────────────────────────────────────────────────────────
  // Fetch all care_plans for this admission and merge into summaryData
  // ────────────────────────────────────────────────────────────────────
  const fetchAndMergeCarePlans = async (base) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
    try {
      const res = await axios.get(
        `${BACKEND_URL}care_plans/?admission_id=${admissionId}`,
        { headers: authHeaders }
      );
      const payload = res?.data;
      const tryArr = (v) => (Array.isArray(v) ? v : null);
      const d = payload?.data;
      const list =
        tryArr(d?.care_plans) ||
        tryArr(d?.care_plan_entries) ||
        tryArr(d?.entries) ||
        tryArr(d?.results) ||
        tryArr(d?.care_plan_list) ||
        tryArr(d?.data?.care_plans) ||
        tryArr(d?.data) ||
        tryArr(payload?.care_plans) ||
        tryArr(payload?.care_plan_list) ||
        tryArr(payload?.results) ||
        tryArr(d) ||
        tryArr(payload) ||
        [];
      if (!Array.isArray(list) || list.length === 0) return base;

      const newMeds = [];
      const newTests = [];
      let dietType = "";
      const dietRecs = [];
      const monitoringLines = [];
      const clinicalLines = [];

      list.forEach((item) => {
        const data = item?.data || {};
        const sName = String(item?.structure_name || item?.type || "").toLowerCase();

        const isMed =
          sName.includes("medication") || sName.includes("medicine") ||
          !!(data?.medication && typeof data.medication === "object") ||
          (Array.isArray(data?.medications) && data.medications.length > 0);
        const isTest =
          sName.includes("test") ||
          !!(data?.testName || (data?.test && typeof data.test === "object")) ||
          (Array.isArray(data?.tests) && data.tests.length > 0);
        const isNutrition =
          sName.includes("nutrition") || sName.includes("diet") || !!(data?.oralDiet);
        const isMonitoring =
          sName.includes("monitoring") || !!(data?.monitoringItems || data?.parameter);
        const isClinical =
          sName.includes("clinical") || sName.includes("notes") ||
          !!(data?.condition || (data?.notes && !isMed && !isTest && !isNutrition && !isMonitoring));

        if (isMed) {
          // Handle medications array format (manual modal: data.medications[])
          if (Array.isArray(data?.medications) && data.medications.length > 0) {
            data.medications.forEach((m) => {
              const name = String(m?.drugName || m?.name || m?.drug_name || "").trim();
              if (name) {
                newMeds.push({
                  name,
                  dose: String(m?.dose || ""),
                  frequency: String(m?.frequency || ""),
                  start: String(m?.startDate || m?.start || ""),
                  period: String(m?.duration || m?.period || ""),
                  periodUnit: String(m?.periodUnit || ""),
                  purpose: String(m?.indication || m?.reason || ""),
                });
              }
            });
          } else {
            // Handle single medication object format (voice assistant:
            // data.medication, doctor console: data.medication.drug_name).
            // Extracted so the key mapping is testable — see
            // utils/carePlanMedications.js.
            const mapped = mapCarePlanMedication(data);
            if (mapped) newMeds.push(mapped);
          }
        } else if (isTest) {
          // Handle tests array format (manual modal: data.tests[])
          if (Array.isArray(data?.tests) && data.tests.length > 0) {
            data.tests.forEach((t) => {
              const tName = String(t?.testName || t?.name || "").trim();
              if (tName) newTests.push(tName);
            });
          } else {
            const tName = String(data?.testName || data?.test?.name || data?.name || "").trim();
            if (tName) newTests.push(tName);
          }
        } else if (isNutrition) {
          const oral = data?.oralDiet || {};
          if (!dietType && oral.dietType) dietType = oral.dietType;
          if (oral.instruction) dietRecs.push(oral.instruction);
          if (oral.nutrients) dietRecs.push(`Nutrients: ${oral.nutrients}`);
          if (data?.allergies) dietRecs.push(`Allergies: ${data.allergies}`);
          if (data?.excludeFoods) dietRecs.push(`Exclude: ${data.excludeFoods}`);
          if (data?.foodPreferences) dietRecs.push(`Preferences: ${data.foodPreferences}`);
        } else if (isMonitoring) {
          if (Array.isArray(data?.monitoringItems)) {
            data.monitoringItems.forEach((m) => {
              if (m.parameter) monitoringLines.push(`${m.parameter}${m.frequency ? ` (${m.frequency})` : ""}`);
            });
          } else if (data?.parameter) {
            monitoringLines.push(`${data.parameter}${data.frequency ? ` (${data.frequency})` : ""}`);
          }
        } else if (isClinical) {
          const note = String(data?.condition || data?.notes || "").trim();
          if (note) clinicalLines.push(note);
        }
      });

      const merged = JSON.parse(JSON.stringify(base));

      // Medications → treatment_given.medications_administered ONLY.
      //
      // These are the factual record of what was given. They are deliberately
      // NOT copied into advices.medications_to_continue: that list is what the
      // patient takes home, and nothing here distinguishes a stopped drug, a
      // one-off pre-op antibiotic or a PRN from a continuing medication. The
      // doctor opts each one in via the "Copy from administered…" picker.
      if (newMeds.length > 0) {
        newMeds.forEach((m) =>
          carePlanMedNamesRef.current.add(m.name.toLowerCase().trim())
        );
        if (!merged.treatment_given) merged.treatment_given = {};
        const existAdm = getNonEmptyMedications(merged.treatment_given?.medications_administered);
        const existNames = new Set(existAdm.map((m) => (m.name || m.drug_name || "").toLowerCase().trim()));
        merged.treatment_given.medications_administered = [
          ...existAdm,
          ...newMeds.filter((m) => !existNames.has(m.name.toLowerCase().trim())),
        ];
      }

      // Tests → investigations
      if (newTests.length > 0) {
        const existInv = getNonEmptyItems(merged?.investigations);
        const existSet = new Set(existInv.map((t) => t.toLowerCase().trim()));
        merged.investigations = [
          ...existInv,
          ...newTests.filter((t) => !existSet.has(t.toLowerCase().trim())),
        ];
      }

      // Nutrition → advices.diet
      if (dietType || dietRecs.length > 0) {
        if (!merged.advices) merged.advices = {};
        if (!merged.advices.diet) merged.advices.diet = {};
        if (!merged.advices.diet.type && dietType) merged.advices.diet.type = dietType;
        const existRecs = getNonEmptyItems(merged.advices.diet?.recommendations);
        const existRecsSet = new Set(existRecs.map((r) => r.toLowerCase().trim()));
        merged.advices.diet.recommendations = [
          ...existRecs,
          ...dietRecs.filter((r) => !existRecsSet.has(r.toLowerCase().trim())),
        ];
      }

      // Monitoring → treatment_given.supportive_care
      if (monitoringLines.length > 0) {
        if (!merged.treatment_given) merged.treatment_given = {};
        const existSup = getNonEmptyItems(merged.treatment_given?.supportive_care);
        const existSet = new Set(existSup.map((s) => s.toLowerCase().trim()));
        merged.treatment_given.supportive_care = [
          ...existSup,
          ...monitoringLines.filter((s) => !existSet.has(s.toLowerCase().trim())),
        ];
      }

      // Clinical notes → history.history_of_present_illness (append if empty)
      if (clinicalLines.length > 0 && !merged?.history?.history_of_present_illness) {
        if (!merged.history) merged.history = {};
        merged.history.history_of_present_illness = clinicalLines.join("\n");
      }

      return merged;
    } catch (err) {
      console.error("[DischargeSummary] Failed to merge care plans:", err?.message);
      return base;
    }
  };

  // ────────────────────────────────────────────────────────────────────
  // Fill the discharge summary's vital-signs slot from the last vitals the
  // nurse recorded.
  //
  // The slot has always existed — validateDischargePayload defaults it, and
  // jsonToHtmlParser renders it — and nothing ever populated it. So the doctor
  // either retyped observations that were already in the chart, or shipped the
  // summary with the field blank.
  //
  // Fills blanks only: a value the generate endpoint or the doctor supplied
  // always wins. recorded_at rides along because a vital sign without a time is
  // not clinically usable on a discharge summary.
  // ────────────────────────────────────────────────────────────────────
  const mergeFinalVitals = async (base) => {
    try {
      const res = await getVitals(admissionId, { limit: 1 });
      const latest = listOf(res)[0];
      if (!latest) return base;

      const merged = JSON.parse(JSON.stringify(base));
      if (!merged.header) merged.header = {};
      if (!merged.header.discharge_details) merged.header.discharge_details = {};

      const existing = merged.header.discharge_details.vital_signs;
      const vs =
        existing && typeof existing === "object" && !Array.isArray(existing)
          ? { ...existing }
          : {};

      const fill = (key, value) => {
        if (value === null || value === undefined || value === "") return;
        const current = vs[key];
        if (current === null || current === undefined || String(current).trim() === "") {
          vs[key] = String(value);
        }
      };

      fill("bp", latest.blood_pressure);
      fill("pulse", latest.heart_rate);
      fill("temperature", latest.temperature);
      fill("spo2", latest.oxygen_saturation);
      fill("respiratory_rate", latest.respiratory_rate);
      fill("consciousness", latest.consciousness);
      fill("recorded_at", latest.recorded_at);
      fill("recorded_by", latest.recorded_by_name);

      merged.header.discharge_details.vital_signs = vs;
      return merged;
    } catch (err) {
      // Never fatal: a summary without vitals is worse than one with them, but
      // far better than no summary at all.
      console.warn("[DischargeSummary] Could not load final vitals:", err?.message);
      return base;
    }
  };

  // ────────────────────────────────────────────────────────────────────
  // STEP 0 → STEP 1 : Template selected → fetch clinical data
  // ────────────────────────────────────────────────────────────────────
  // Applying a template regenerates summaryData from the care plans, which
  // overwrites every Step-1 edit. That used to happen with no dirty check and
  // no warning at all, so reselecting a template silently destroyed the form.
  const handleTemplateSelected = (templateId, templateObj) => {
    if (summaryData && dirtyRef.current) {
      setPendingTemplate({ templateId, templateObj });
      return;
    }
    applyTemplateSelection(templateId, templateObj);
  };

  const applyTemplateSelection = async (templateId, templateObj) => {
    setSelectedTemplateObj(templateObj);
    setLoading(true);
    setErrorMessage("");
    setCurrentStep(1);

    try {
      const response = await DischargeSummaryService.generateSummary(
        admissionId,
        templateId,
        authHeaders
      );

      // Support multiple (and nested) response shapes — flatten to the summary object.
      const data = normalizeSummaryShape(response);

      // Step 1 is the RAW clinical summary (care plans + patient journey). The LLM
      // articulation (validate_discharge_payload) deliberately runs later, on Compile —
      // so the sentence-forming pass runs once, against the data the doctor actually
      // reviewed/edited here, rather than pre-articulating before review.
      const withCarePlans = await fetchAndMergeCarePlans(data);
      const finalData = await mergeFinalVitals(withCarePlans);
      setSummaryData(finalData);
    } catch (err) {
      console.error("Failed to generate discharge summary:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load discharge summary."
      );
    } finally {
      setLoading(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────
  // STEP 1 → STEP 2 : Compile clinical summary → validate → editor
  // ────────────────────────────────────────────────────────────────────
  /**
   * Core compile: turn the reviewed clinical summary into the articulated discharge
   * document and advance to the editor.
   *
   * When `regenerateHtml` is true we run the backend LLM articulation
   * (validate_discharge_payload) — this is the sentence-forming step that turns the
   * structured care-plan/journey data into prose — then build the editor HTML from
   * that articulated payload. When false (the "Keep Edits" path) we skip both and
   * return to the doctor's existing edited document untouched.
   */
  /**
   * Build the editor HTML from a settled payload and enter the editor.
   *
   * Split out of compileToEditor so both diff outcomes — the generated wording
   * and the doctor's own — go through exactly one path.
   */
  const buildDocumentFrom = (payload) => {
    try {
      const rawHtml =
        selectedTemplateObj?.content ||
        selectedTemplateObj?.template_html ||
        selectedTemplateObj?.html_content ||
        "";
      // For a deceased patient, expose the MCCD under `death.*` so a death
      // template's placeholders ({{cause_of_death}}, {{underlying_cause}},
      // {{manner_of_death}}, {{certifying_doctor}}, {{time_of_death}},
      // {{cod_1a}}/{{cod_1a_interval}}…) hydrate automatically — the HTAI
      // service doesn't see DischargeProcess, so the cause must be injected
      // here or the section renders blank. Harmless for a living patient
      // (no such placeholders in a normal template).
      const hydrationData =
        dischargeProcess?.outcome === "deceased"
          ? { ...payload, death: buildDeathHydration(dischargeProcess) }
          : payload;
      setHtmlContent(hydrateTemplate(rawHtml, hydrationData));
      htmlInitializedRef.current = true;
      // Dirty on purpose: a freshly built document has not been persisted yet,
      // and autosave is what stops it being lost on close.
      dirtyRef.current = true;
      setCurrentStep(2);
      return true;
    } catch (parseErr) {
      console.error("[DischargeSummary] Failed to parse JSON to HTML:", parseErr?.message);
      setErrorMessage("Failed to convert clinical data to document format.");
      return false;
    }
  };

  const handleAcceptArticulation = () => {
    const { enriched } = pendingArticulation || {};
    setPendingArticulation(null);
    if (!enriched) return;
    setSummaryData(enriched);
    buildDocumentFrom(enriched);
  };

  const handleRejectArticulation = () => {
    const { base } = pendingArticulation || {};
    setPendingArticulation(null);
    if (!base) return;
    buildDocumentFrom(base);
  };

  const compileToEditor = async ({ regenerateHtml }) => {
    try {
      setLoading(true);
      setErrorMessage("");

      // Local validation & auto-fix (structure only; non-destructive).
      const localResult = validateDischargePayload(summaryData);
      if (localResult.warnings.length > 0) {
        // Previously console.info, where nobody saw it — the doctor had no idea
        // fields had been silently repaired under them.
        toast.warn(
          `${localResult.warnings.length} field${
            localResult.warnings.length === 1 ? " was" : "s were"
          } auto-corrected while building the document. Review it before finalizing.`
        );
      }
      let cleanedData = localResult.data;
      setSummaryData(cleanedData);

      if (regenerateHtml) {
        // LLM articulation: POST the clinical-summary payload so the backend LLM
        // generates the articulated prose discharge document. This is the
        // sentence-forming step and must run whenever we (re)build the document.
        //
        // Its result is NOT applied here. It used to be written straight over
        // the doctor's reviewed data, so they signed sentences they had never
        // seen as a change. It now goes to the diff dialog, which decides.
        setArticulating(true);
        let enriched = null;
        try {
          const serverResult = await DischargeSummaryService.validatePayload(
            { ...cleanedData, admission_id: admissionId },
            authHeaders
          );
          const candidate = normalizeSummaryShape(serverResult);
          if (looksLikeSummary(candidate)) enriched = candidate;
        } catch (valErr) {
          console.warn("[DischargeSummary] LLM articulation failed:", valErr?.message);
        } finally {
          setArticulating(false);
        }

        if (!enriched) {
          // Previously swallowed to console.warn — the doctor got a materially
          // different document with no notice whatsoever.
          toast.warn(
            "Couldn't generate the narrative summary. The document was built from your wording — review it before finalizing."
          );
          buildDocumentFrom(cleanedData);
          return;
        }

        const changes = computeArticulationDiff(cleanedData, enriched);
        if (changes.length === 0) {
          setSummaryData(enriched);
          buildDocumentFrom(enriched);
          return;
        }
        setPendingArticulation({ base: cleanedData, enriched, changes });
        return;
      }

      setCurrentStep(2);
    } catch (err) {
      console.error("Failed to compile summary:", err?.message);
      setErrorMessage("Failed to compile clinical summary.");
    } finally {
      setLoading(false);
    }
  };

  const handleCompileSummary = async () => {
    // Re-entering the editor after edits: regenerating from the form would discard
    // the doctor's document edits, so confirm first. First time through, just build.
    if (htmlInitializedRef.current) {
      setShowRecompileConfirm(true);
      return;
    }
    await compileToEditor({ regenerateHtml: true });
  };

  // Confirm dialog: regenerate the document from the form, discarding editor edits.
  const handleConfirmRecompile = async () => {
    setShowRecompileConfirm(false);
    await compileToEditor({ regenerateHtml: true });
  };

  // Confirm dialog: keep the existing editor HTML and just return to the editor.
  const handleKeepEditsAndContinue = async () => {
    setShowRecompileConfirm(false);
    await compileToEditor({ regenerateHtml: false });
  };

  // ────────────────────────────────────────────────────────────────────
  // STEP 2 : Save Draft
  // ────────────────────────────────────────────────────────────────────
  const handleSaveDraft = async (silent = false) => {
    if (!silent) setSavingDraft(true);
    try {
      // Pull the editor's live HTML directly so a not-yet-flushed edit can't be missed.
      const latestHtml = editorRef.current?.getHTML?.() || htmlContent;
      // Send the edited HTML BOTH as a top-level sibling AND embedded inside
      // final_json. final_json is the one blob the save endpoint is documented to
      // persist (main_finaldischarge_summery), so embedding guarantees the HTML
      // round-trips even if the separate html_content store isn't wired. We keep the
      // top-level key too in case the backend reads it for get_edited_discharge_summary.
      const savePayload = {
        admission_id: parseInt(admissionId, 10),
        final_json: {
          ...(summaryData || {}),
          html_content: latestHtml,
          // Rides with the draft so a resume can rebuild against the layout the
          // doctor actually chose. Stripped again in loadDraft.
          __template_id: selectedTemplateId(),
        },
        html_content: latestHtml,
        status: "DRAFT"
      };

      const saveRes = await DischargeSummaryService.saveDischargeSummary(savePayload, authHeaders);

      // Backend returns 200 {status:"Failed"} for business errors — axios never
      // rejects for these, so the envelope must be checked explicitly. Otherwise
      // leave dirtyRef TRUE so the next autosave / close-flush retries instead of
      // the edit silently vanishing.
      if (saveRes?.status !== "Success") {
        console.error("Failed to save draft:", saveRes?.message);
        if (!silent) {
          toast.error(saveRes?.message || "Failed to save draft.");
        }
        return false;
      }

      dirtyRef.current = false;
      setLastSavedAt(new Date().toLocaleTimeString());
      if (!silent) toast.success("Draft saved successfully.");
      return true;
    } catch (err) {
      console.error("Failed to save draft:", err?.message);
      if (!silent) {
        toast.error(
          err?.response?.data?.message || "Failed to save draft."
        );
      }
      return false;
    } finally {
      if (!silent) setSavingDraft(false);
    }
  };

  // Editor change handler: mark the document dirty and mirror the HTML into state
  // (state is the fallback source for finalize when the editor is unmounted).
  const handleEditorChange = (html) => {
    dirtyRef.current = true;
    setHtmlContent(html);
  };

  // Debounced autosave while editing (Steps 1 and 2). Persists silently ~3s
  // after the last change so work survives navigation/close without an explicit
  // Save Draft click.
  //
  // Step 1 is included because everything typed into the clinical form before
  // Compile used to be discarded on close: the flush below was gated on
  // htmlInitializedRef, which is only set once the document has been built.
  useEffect(() => {
    if (currentStep !== 1 && currentStep !== 2) return;
    if (!dirtyRef.current) return;
    const timer = setTimeout(() => {
      // Re-check at fire time so a manual Save Draft in the interim doesn't trigger
      // a redundant autosave.
      if (dirtyRef.current) handleSaveDraft(true);
    }, 3000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryData, htmlContent, currentStep]);

  // Close handler: flush any unsaved change and WAIT for it.
  //
  // This used to fire the save without awaiting and unmount immediately, so a
  // failure was invisible and the edit was simply gone. It was also gated on
  // htmlInitializedRef, which meant nothing typed before Compile was ever
  // flushed at all.
  const handleClose = async () => {
    if (dirtyRef.current) {
      setSavingDraft(true);
      let saved = false;
      try {
        saved = await handleSaveDraft(true);
      } finally {
        setSavingDraft(false);
      }
      if (!saved) {
        const discard = window.confirm(
          "Your changes could not be saved. Close anyway and lose them?"
        );
        if (!discard) return;
      }
    }
    onClose();
  };



  // ────────────────────────────────────────────────────────────────────
  // STEP 2 → STEP 3 : Proceed to preview
  // ────────────────────────────────────────────────────────────────────
  const handleProceedToPreview = () => {
    setCurrentStep(3);
  };

  // ────────────────────────────────────────────────────────────────────
  // STEP 3 : Finalize — save final + generate PDF + upload to S3
  // ────────────────────────────────────────────────────────────────────
  const handleFinalize = async () => {
    setSubmitting(true);
    setErrorMessage("");

    try {
      // Pull the editor's live HTML directly so the PDF and saved record reflect the latest edits.
      const latestHtml = editorRef.current?.getHTML?.() || htmlContent;

      // 1. Generate PDF from HTML content
      const pdfBlob = await generatePdfBlobFromHtml(latestHtml, printMode);

      // Get the correct filename
      const patientSafeName = (patientName || summaryData?.header?.name || "Patient").replace(/[^a-zA-Z0-9]/g, "_");
      const filename = `Discharge_Summary_${patientSafeName}_${admissionId}.pdf`;

      // 2. Upload PDF to S3
      const uploadRes = await DischargeSummaryService.uploadDischargePdf(
        admissionId,
        pdfBlob,
        filename,
        authHeaders
      );
      // Backend returns 200 {status:"Failed"} for S3/business errors — axios never
      // rejects for these, so the envelope must be checked before treating the
      // upload as done.
      if (uploadRes?.status !== "Success") {
        const failMsg =
          (uploadRes?.message || "Failed to upload discharge PDF.") +
          " Nothing was saved — press Finalize again to retry.";
        setErrorMessage(failMsg);
        toast.error(failMsg);
        return;
      }

      // 3. Save final summary (embed HTML in final_json too — see handleSaveDraft).
      const savePayload = {
        admission_id: parseInt(admissionId, 10),
        final_json: {
          ...(summaryData || {}),
          html_content: latestHtml,
          __template_id: selectedTemplateId(),
        },
        html_content: latestHtml,
        status: "FINALIZED"
      };

      const saveRes = await DischargeSummaryService.saveDischargeSummary(
        savePayload,
        authHeaders
      );
      if (saveRes?.status !== "Success") {
        // The two halves fail differently and the fix differs: an upload failure
        // leaves nothing behind, a save failure leaves a PDF in S3 that the
        // record does not acknowledge. Say which happened.
        const failMsg =
          (saveRes?.message || "Failed to save the finalized discharge summary.") +
          " The PDF was uploaded but the record was not saved — press Finalize again to retry.";
        setErrorMessage(failMsg);
        toast.error(failMsg);
        return;
      }

      toast.success("Discharge summary finalized and uploaded successfully!");
      // Finalizing is what unlocks Approve, so record it and refresh rather than
      // closing blind — the doctor's next action is on this screen.
      setHasFinalizedDocument(true);
      dirtyRef.current = false;
      loadDischargeProcess();

      // 4. Auto-print after successful finalization — as it always did. The
      //    only addition is the download fallback below: a blocked popup used
      //    to leave the user with a toast and no way to reach the document.
      try {
        const url = URL.createObjectURL(pdfBlob);
        const printWindow = window.open(url, "_blank");
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
          // Revoke late so a slow viewer doesn't lose the blob mid-load.
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        } else {
          // Popup blocked — fall back to a download so the document is never
          // simply unreachable. The old code left the user with a toast and no
          // way to get the PDF at all.
          const a = document.createElement("a");
          a.href = url;
          a.download = `Discharge_Summary_${(patientName || "Patient").replace(
            /[^a-zA-Z0-9]/g,
            "_"
          )}_${admissionId}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 60000);
          toast.info("Popup blocked — the discharge summary was downloaded instead.");
        }
      } catch (printErr) {
        console.error("Failed to open the finalized PDF:", printErr?.message);
        toast.warn(
          "The summary was finalized but could not be opened for printing. Use View Summary to open it."
        );
      }
    } catch (err) {
      console.error("Failed to finalize discharge summary:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to finalize discharge summary."
      );
      toast.error("Failed to finalize discharge summary.");
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Generates a PDF Blob from the HTML content using html2pdf.js
   * This ensures the saved PDF exactly matches the edited template.
   *
   * The ONLY change from the original is the letterhead: the inline block that
   * used to draw it here has been replaced by stampLetterheadV5. Everything
   * else — the .clinical-document-preview container class the document's own
   * styling depends on, the off-screen wrapper, the page-break mode and the
   * blank-PDF guard — is unchanged, because it worked.
   */
  const generatePdfBlobFromHtml = async (html, mode = "blank") => {
    return new Promise((resolve, reject) => {
      try {
        const container = document.createElement("div");
        // Appended here rather than merged into htmlContent, so the annexure
        // reaches the PDF without ever entering the saved/edited document.
        container.innerHTML = html + marAnnexureHtml;
        container.className = "clinical-document-preview tiptap";

        const wrapper = document.createElement("div");
        wrapper.style.position = "absolute";
        wrapper.style.top = "-9999px";
        wrapper.style.left = "-9999px";
        wrapper.style.width = "794px"; // Fixed A4 width at 96 DPI
        wrapper.style.height = "auto"; // Auto height instead of 0
        wrapper.style.zIndex = "-9999";

        // Ensure bright white background and black text
        container.style.backgroundColor = "#ffffff";
        container.style.color = "#000000";
        container.style.width = "100%"; // Uses wrapper's 794px width
        container.style.padding = "10mm"; // Internal padding to prevent cutting off

        wrapper.appendChild(container);
        document.body.appendChild(wrapper);

        // Meaningful naming convention for the generated PDF
        const patientSafeName = (patientName || summaryData?.header?.name || "Patient").replace(/[^a-zA-Z0-9]/g, "_");
        const filename = `Discharge_Summary_${patientSafeName}_${admissionId}.pdf`;

        const opt = {
          // Top margin grew from 35mm to clear the v5 header (rule at 49mm,
          // status pill to 58.5mm); bottom from 30 to clear the footer rule at
          // 268mm. Everything else is as it was.
          margin: [mode === "blank" ? V5.HEADER_H_CHROME_ONLY_MM : 35, 15, 32, 15],
          filename: filename,
          image: { type: "jpeg", quality: 1.0 },
          html2canvas: { scale: 2, useCORS: true, windowWidth: 794 }, // Prevent responsive layouts from breaking
          // jsPDF embeds images as uncompressed raw RGB without this. It is the
          // difference between a ~300KB file and a 14MB one.
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait", compress: true },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };

        html2pdf()
          .set(opt)
          .from(container)
          .toPdf()
          .get('pdf')
          .then((pdf) => {
            // mode === "letterhead" means pre-printed stationery goes through
            // the tray, so nothing is drawn — but the page box is unchanged, so
            // the content still lands inside the pre-printed header and footer.
            if (mode === "blank") {
              const facility = resolveFacilityDetails(userInfo);
              stampLetterheadV5(pdf, {
                logoBase64: resolveLogo(brandingData),
                facilityName:
                  userInfo?.facility_name ||
                  summaryData?.hospital_details?.name ||
                  facility.address.split(",")[0] ||
                  "",
                address: summaryData?.hospital_details?.address || facility.address,
                contact:
                  summaryData?.hospital_details?.contact ||
                  (facility.contact === "N/A" ? "" : facility.contact),
                // Page-2+ "(continued)" strip only. The templates already open
                // with their own heading, so drawing one here printed it twice.
                documentTitle:
                  dischargeProcess?.outcome === "deceased"
                    ? "Death Summary"
                    : "Discharge Summary",
                titleOnPage1: false,
                statusLabel: String(dischargeProcess?.summary_status || "draft")
                  .replace(/_/g, " ")
                  .toUpperCase(),
                statusColor:
                  dischargeProcess?.summary_status === "approved"
                    ? V5.COLORS.success
                    : V5.COLORS.danger,
                // prepared_by is routinely blank, which printed a dangling
                // "Prepared by · " on a patient-facing document. Fall back to
                // the consultant, then whoever is signed in.
                preparedBy:
                  summaryData?.prepared_by?.name ||
                  summaryData?.header?.consultant_incharge ||
                  sessionStorage.getItem("userName") ||
                  "",
                checkedBy: summaryData?.checked_by?.name || "",
              });
            }
          })
          .output("blob")
          .then((blob) => {
            document.body.removeChild(wrapper);
            if (blob.size < 5000) {
              reject(new Error("Generated PDF is suspiciously small or blank. Please try again."));
            } else {
              resolve(blob);
            }
          })
          .catch((err) => {
            document.body.removeChild(wrapper);
            reject(err);
          });
      } catch (err) {
        reject(err);
      }
    });
  };

// ── Deep update helpers (same as before) ─────────────────────────────

  const handleUpdate = (path, value) => {
    dirtyRef.current = true;
    setSummaryData((prev) => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;
      const keys = path.split('.');
      for (let i = 0; i < keys.length - 1; i++) {
        if (current[keys[i]] === undefined || current[keys[i]] === null) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleArrayUpdate = (path, index, value) => {
    dirtyRef.current = true;
    setSummaryData((prev) => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;
      const keys = path.split('.');
      for (let i = 0; i < keys.length - 1; i++) {
        if (current[keys[i]] === undefined || current[keys[i]] === null) current[keys[i]] = {};
        current = current[keys[i]];
      }
      if (!Array.isArray(current[keys[keys.length - 1]])) {
        current[keys[keys.length - 1]] = [];
      }
      current[keys[keys.length - 1]][index] = value;
      return newData;
    });
  };

  const handleArrayObjectUpdate = (path, index, field, value) => {
    dirtyRef.current = true;
    setSummaryData((prev) => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;
      const keys = path.split('.');
      for (let i = 0; i < keys.length - 1; i++) {
        if (current[keys[i]] === undefined || current[keys[i]] === null) current[keys[i]] = {};
        current = current[keys[i]];
      }
      if (!Array.isArray(current[keys[keys.length - 1]])) {
        current[keys[keys.length - 1]] = [];
      }
      if (!current[keys[keys.length - 1]][index]) {
        current[keys[keys.length - 1]][index] = {};
      }
      current[keys[keys.length - 1]][index][field] = value;
      return newData;
    });
  };

  // Resolve (creating intermediates as needed) the array at `path` in a cloned
  // summary, run `mutate(arr)`, and commit. Backs the add/remove controls.
  const mutateArrayAtPath = (path, mutate) => {
    dirtyRef.current = true;
    setSummaryData((prev) => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;
      const keys = path.split('.');
      for (let i = 0; i < keys.length - 1; i++) {
        if (current[keys[i]] === undefined || current[keys[i]] === null) current[keys[i]] = {};
        current = current[keys[i]];
      }
      const lastKey = keys[keys.length - 1];
      if (!Array.isArray(current[lastKey])) current[lastKey] = [];
      mutate(current[lastKey]);
      return newData;
    });
  };

  const handleArrayAdd = (path, emptyValue = "") =>
    mutateArrayAtPath(path, (arr) => arr.push(emptyValue));

  const handleArrayRemove = (path, index) =>
    mutateArrayAtPath(path, (arr) => arr.splice(index, 1));

  // Append the doctor's chosen administered medications to the take-home list,
  // skipping any already there so re-running the picker cannot duplicate rows.
  const handleCopyMedications = (selected) => {
    setCopyMedsOpen(false);
    if (!selected.length) return;
    mutateArrayAtPath("advices.medications_to_continue", (arr) => {
      const existing = new Set(
        arr.map((m) => (m?.name || m?.drug_name || "").toLowerCase().trim())
      );
      selected.forEach((m) => {
        const key = (m?.name || m?.drug_name || "").toLowerCase().trim();
        if (key && !existing.has(key)) {
          arr.push({ ...m });
          existing.add(key);
        }
      });
    });
  };

  const handleMedicationAdd = (path) =>
    handleArrayAdd(path, { name: "", dose: "", frequency: "", start: "", period: "", periodUnit: "", purpose: "" });

  // ── Handle back navigation ──────────────────────────────────────────
  const handleBack = () => {
    if (currentStep <= 0) return;
    // Step 0 re-selects a template and regenerates summaryData. Once the
    // document exists that is a rebuild, not a back step — and "Rebuild from
    // Form" is the control for it — so it is not reachable this way.
    if (currentStep === 1 && htmlInitializedRef.current) {
      setCurrentStep(2);
      return;
    }
    setCurrentStep((prev) => prev - 1);
    setErrorMessage("");
  };

  // ════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
    >
      {/* ── Title bar ───────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          fontWeight: 700,
          borderBottom: "1px solid #ddd",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {currentStep > 0 && (
            <IconButton
              size="small"
              onClick={handleBack}
              disabled={loading || submitting}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <span>
            Discharge Summary{patientName ? ` - ${patientName}` : ""}
          </span>
          {selectedTemplateObj && (
            <Chip
              size="small"
              variant="outlined"
              label={`Template: ${
                selectedTemplateObj.template_name ||
                selectedTemplateObj.name ||
                selectedTemplateObj.title ||
                "Selected"
              }`}
            />
          )}
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ── Stepper ─────────────────────────────────────────────────── */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #eee" }}>
        <Stepper activeStep={currentStep}>
          {STEP_LABELS.map((label) => (
            <Step key={label}>
              <StepLabel StepIconProps={{ sx: { width: 22, height: 22 } }}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{label}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* ── Approval status (Task C15) — persistent across all steps ──── */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Approval Status:
          </Typography>
          {dischargeProcess === undefined ? (
            <Chip size="small" variant="outlined" label="Loading…" />
          ) : dischargeProcess ? (
            <Chip
              size="small"
              label={
                SUMMARY_STATUS_LABELS[dischargeProcess.summary_status] ||
                dischargeProcess.summary_status
              }
              color={SUMMARY_STATUS_CHIP_COLOR[dischargeProcess.summary_status] || "default"}
            />
          ) : (
            <Chip size="small" variant="outlined" label="No Discharge Process" />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {dischargeProcess === null && (
            <Typography variant="caption" color="text.secondary">
              Initiate discharge for this admission before submitting a summary for approval.
            </Typography>
          )}
          {dischargeProcess?.summary_status === "draft" && (
            <Tooltip title={hasSavedDraft ? "" : "Save a draft first."}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleUpdateSummaryStatus("pending_approval")}
                  disabled={updatingSummaryStatus || !hasSavedDraft}
                >
                  {updatingSummaryStatus ? "Submitting…" : "Submit for Approval"}
                </Button>
              </span>
            </Tooltip>
          )}
          {dischargeProcess?.summary_status === "pending_approval" && (
            <>
              {!allowedToApprove && (
                <Typography variant="caption" color="warning.main">
                  Only the admitting doctor can approve — you can still try; the server will confirm.
                </Typography>
              )}
              {/* MUI swallows tooltips on disabled buttons, hence the span. */}
              <Tooltip title={approveBlockedReason}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => handleUpdateSummaryStatus("approved")}
                    disabled={updatingSummaryStatus || Boolean(approveBlockedReason)}
                  >
                    Approve
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleUpdateSummaryStatus("rejected")}
                disabled={updatingSummaryStatus}
              >
                Reject
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* ── Content ─────────────────────────────────────────────────── */}
      <DialogContent
        sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 }}
      >
        {/* ─── STEP 0: Template Selection ─────────────────────────── */}
        {currentStep === 0 && (
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {checkingDraft ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  py: 6,
                  flex: 1,
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Checking for drafts...</Typography>
              </Box>
            ) : (
              <TemplateSelectionStep
                admissionId={admissionId}
                authHeaders={authHeaders}
                onTemplateSelected={handleTemplateSelected}
              />
            )}
          </Box>
        )}

        {/* ─── STEP 1: Clinical Summary Review ────────────────────── */}
        {currentStep === 1 && (
          <>
            {loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  py: 6,
                  flex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {!loading && errorMessage && (
              <Box sx={{ p: 3 }}>
                <Alert severity="error">{errorMessage}</Alert>
              </Box>
            )}

            {!loading && !errorMessage && summaryData && (
              <Grid
                container
                sx={{ height: "100%", flexWrap: "nowrap", flex: 1 }}
              >
                {/* Form Side */}
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    height: "100%",
                    overflowY: "auto",
                    p: 3,
                    borderRight: "1px solid #ddd",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Box sx={{ mb: 1.5 }}>
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={
                        summaryData?.document_type ||
                        "Patient Discharge Summary"
                      }
                    />
                  </Box>

                  {formLocked && (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      This document has been compiled. The form below is what it was
                      built from — edit the document in the next step, or rebuild it
                      from the form.
                    </Alert>
                  )}

                  {/* A disabled fieldset disables every descendant control, which
                      is the whole form in one place rather than a `disabled` prop
                      threaded through forty fields. */}
                  <Box
                    component="fieldset"
                    disabled={formLocked}
                    sx={{
                      border: 0,
                      p: 0,
                      m: 0,
                      minWidth: 0,
                      opacity: formLocked ? 0.6 : 1,
                      pointerEvents: formLocked ? "none" : "auto",
                    }}
                  >

                  {/* ── Final Diagnosis ── */}
                  <SectionCard title="Final Diagnosis">
                    <EditableStringList
                      items={summaryData?.final_diagnosis}
                      itemLabel="Diagnosis"
                      onItemChange={(i, v) => handleArrayUpdate("final_diagnosis", i, v)}
                      onRemove={(i) => handleArrayRemove("final_diagnosis", i)}
                      onAdd={() => handleArrayAdd("final_diagnosis")}
                    />
                  </SectionCard>

                  {/* ── Investigations ── */}
                  <SectionCard title="Investigations">
                    <EditableStringList
                      items={summaryData?.investigations}
                      itemLabel="Investigation"
                      onItemChange={(i, v) => handleArrayUpdate("investigations", i, v)}
                      onRemove={(i) => handleArrayRemove("investigations", i)}
                      onAdd={() => handleArrayAdd("investigations")}
                    />
                  </SectionCard>

                  {/* ── Hospital Details ── */}
                  <SectionCard title="Hospital Details">
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Name" value={summaryData?.hospital_details?.name} onChange={(v) => handleUpdate('hospital_details.name', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Address" value={summaryData?.hospital_details?.address} onChange={(v) => handleUpdate('hospital_details.address', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Contact" value={summaryData?.hospital_details?.contact} onChange={(v) => handleUpdate('hospital_details.contact', v)} />
                      </Grid>
                    </Grid>
                  </SectionCard>

                  {/* ── Patient & Admission ── */}
                  <SectionCard title="Patient & Admission">
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Patient ID" value={summaryData?.header?.patient_id} onChange={(v) => handleUpdate('header.patient_id', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Patient Name" value={summaryData?.header?.name} onChange={(v) => handleUpdate('header.name', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Age" value={summaryData?.header?.age} onChange={(v) => handleUpdate('header.age', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Gender" value={summaryData?.header?.gender} onChange={(v) => handleUpdate('header.gender', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Bed No" value={summaryData?.header?.bed_no} onChange={(v) => handleUpdate('header.bed_no', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Category" value={summaryData?.header?.category} onChange={(v) => handleUpdate('header.category', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Consultant Incharge" value={summaryData?.header?.consultant_incharge} onChange={(v) => handleUpdate('header.consultant_incharge', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Date of Admission" value={summaryData?.header?.date_of_admission} onChange={(v) => handleUpdate('header.date_of_admission', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Date of Discharge" value={summaryData?.header?.date_of_discharge} onChange={(v) => handleUpdate('header.date_of_discharge', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Discharge Type" value={summaryData?.header?.discharge_type} onChange={(v) => handleUpdate('header.discharge_type', v)} />
                      </Grid>
                    </Grid>
                  </SectionCard>

                  {/* ── Discharge Details ── */}
                  <SectionCard title="Discharge Details">
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Condition At Discharge" value={summaryData?.header?.discharge_details?.condition_at_discharge} onChange={(v) => handleUpdate('header.discharge_details.condition_at_discharge', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Mobility Status" value={summaryData?.header?.discharge_details?.mobility_status} onChange={(v) => handleUpdate('header.discharge_details.mobility_status', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Follow-up Required" value={summaryData?.header?.discharge_details?.follow_up_required} onChange={(v) => handleUpdate('header.discharge_details.follow_up_required', v)} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <ValueRow label="Remarks" value={summaryData?.header?.discharge_details?.remarks} onChange={(v) => handleUpdate('header.discharge_details.remarks', v)} />
                      </Grid>
                    </Grid>
                    <Divider sx={{ my: 1 }} />
                    {/* Pre-filled from the last vitals the nurse recorded. A
                        vital sign without a time isn't clinically usable, so the
                        recording stamp is shown alongside. */}
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Vital Signs
                      {summaryData?.header?.discharge_details?.vital_signs?.recorded_at && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                          {" "}— last recorded{" "}
                          {formatIstDateTime(
                            summaryData.header.discharge_details.vital_signs.recorded_at
                          )}
                          {summaryData.header.discharge_details.vital_signs.recorded_by
                            ? ` by ${summaryData.header.discharge_details.vital_signs.recorded_by}`
                            : ""}
                        </Typography>
                      )}
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={3}>
                        <ValueRow label="BP" value={summaryData?.header?.discharge_details?.vital_signs?.bp} onChange={(v) => handleUpdate('header.discharge_details.vital_signs.bp', v)} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <ValueRow label="Pulse" value={summaryData?.header?.discharge_details?.vital_signs?.pulse} onChange={(v) => handleUpdate('header.discharge_details.vital_signs.pulse', v)} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <ValueRow label="Temperature" value={summaryData?.header?.discharge_details?.vital_signs?.temperature} onChange={(v) => handleUpdate('header.discharge_details.vital_signs.temperature', v)} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <ValueRow label="SpO2" value={summaryData?.header?.discharge_details?.vital_signs?.spo2} onChange={(v) => handleUpdate('header.discharge_details.vital_signs.spo2', v)} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <ValueRow label="Resp. Rate" value={summaryData?.header?.discharge_details?.vital_signs?.respiratory_rate} onChange={(v) => handleUpdate('header.discharge_details.vital_signs.respiratory_rate', v)} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <ValueRow label="Consciousness" value={summaryData?.header?.discharge_details?.vital_signs?.consciousness} onChange={(v) => handleUpdate('header.discharge_details.vital_signs.consciousness', v)} />
                      </Grid>
                    </Grid>
                  </SectionCard>

                  {/* ── History ── */}
                  <SectionCard title="History">
                    <ValueRow label="History of Present Illness" value={summaryData?.history?.history_of_present_illness} onChange={(v) => handleUpdate('history.history_of_present_illness', v)} />
                    <ValueRow label="Allergies" value={summaryData?.history?.allergies} onChange={(v) => handleUpdate('history.allergies', v)} />
                    <ValueRow label="Family History" value={summaryData?.history?.family_history} onChange={(v) => handleUpdate('history.family_history', v)} />
                    {/* These two used to hand-roll join/split because ValueRow
                        destroyed arrays. It preserves them now, so the plain
                        form is correct for every field. */}
                    <ValueRow label="Past Medical History" value={summaryData?.history?.past_medical_history} onChange={(v) => handleUpdate('history.past_medical_history', v)} />
                    <ValueRow label="Past Surgical History" value={summaryData?.history?.past_surgical_history} onChange={(v) => handleUpdate('history.past_surgical_history', v)} />

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Presenting Complaints</Typography>
                    <EditableStringList
                      items={summaryData?.history?.presenting_complaints}
                      itemLabel="Complaint"
                      onItemChange={(i, v) => handleArrayUpdate("history.presenting_complaints", i, v)}
                      onRemove={(i) => handleArrayRemove("history.presenting_complaints", i)}
                      onAdd={() => handleArrayAdd("history.presenting_complaints")}
                    />

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Personal History</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Diet" value={summaryData?.history?.personal_history?.diet} onChange={(v) => handleUpdate('history.personal_history.diet', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Smoking" value={summaryData?.history?.personal_history?.smoking} onChange={(v) => handleUpdate('history.personal_history.smoking', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Alcohol" value={summaryData?.history?.personal_history?.alcohol} onChange={(v) => handleUpdate('history.personal_history.alcohol', v)} />
                      </Grid>
                    </Grid>
                  </SectionCard>

                  {/* ── Treatment Given ── */}
                  <SectionCard title="Treatment Given">
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Procedures</Typography>
                    <EditableStringList
                      items={summaryData?.treatment_given?.procedures}
                      itemLabel="Procedure"
                      onItemChange={(i, v) => handleArrayUpdate("treatment_given.procedures", i, v)}
                      onRemove={(i) => handleArrayRemove("treatment_given.procedures", i)}
                      onAdd={() => handleArrayAdd("treatment_given.procedures")}
                    />

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Medications Administered</Typography>
                    {asArray(summaryData?.treatment_given?.medications_administered).map((item, index) => (
                      <Box key={`med-${index}`} sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                            Medication {index + 1}
                            {/* Say which rows the doctor didn't type. */}
                            {carePlanMedNamesRef.current.has(
                              String(item?.name || item?.drug_name || "").toLowerCase().trim()
                            ) && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                                {" "}· from care plan
                              </Typography>
                            )}
                          </Typography>
                          <IconButton size="small" onClick={() => handleArrayRemove('treatment_given.medications_administered', index)} aria-label={`Remove medication ${index + 1}`}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <ValueRow label="Name" value={item?.name || item?.drug_name} onChange={(v) => handleArrayObjectUpdate('treatment_given.medications_administered', index, 'name', v)} />
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={3}>
                            <ValueRow label="Dose" value={item?.dose} onChange={(v) => handleArrayObjectUpdate('treatment_given.medications_administered', index, 'dose', v)} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <ValueRow label="Freq" value={item?.frequency} onChange={(v) => handleArrayObjectUpdate('treatment_given.medications_administered', index, 'frequency', v)} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <ValueRow label="Start" value={item?.start} onChange={(v) => handleArrayObjectUpdate('treatment_given.medications_administered', index, 'start', v)} />
                          </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={4}>
                            <ValueRow label="Period" value={item?.period} onChange={(v) => handleArrayObjectUpdate('treatment_given.medications_administered', index, 'period', v)} />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <ValueRow label="Unit" value={item?.periodUnit} onChange={(v) => handleArrayObjectUpdate('treatment_given.medications_administered', index, 'periodUnit', v)} />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <ValueRow label="Purpose" value={item?.purpose} onChange={(v) => handleArrayObjectUpdate('treatment_given.medications_administered', index, 'purpose', v)} />
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={() => handleMedicationAdd('treatment_given.medications_administered')} sx={{ mt: 0.5 }}>
                      Add Medication
                    </Button>

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Supportive Care</Typography>
                    <EditableStringList
                      items={summaryData?.treatment_given?.supportive_care}
                      itemLabel="Supportive Care"
                      onItemChange={(i, v) => handleArrayUpdate("treatment_given.supportive_care", i, v)}
                      onRemove={(i) => handleArrayRemove("treatment_given.supportive_care", i)}
                      onAdd={() => handleArrayAdd("treatment_given.supportive_care")}
                    />
                  </SectionCard>

                  {/* ── Advices ── */}
                  <SectionCard title="Advices">
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Medications To Continue</Typography>
                    {asArray(summaryData?.advices?.medications_to_continue).map((item, index) => (
                      <Box key={`cont-med-${index}`} sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>Medication {index + 1}</Typography>
                          <IconButton size="small" onClick={() => handleArrayRemove('advices.medications_to_continue', index)} aria-label={`Remove medication ${index + 1}`}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <ValueRow label="Name" value={item?.name || item?.drug_name} onChange={(v) => handleArrayObjectUpdate('advices.medications_to_continue', index, 'name', v)} />
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={4}>
                            <ValueRow label="Dose" value={item?.dose} onChange={(v) => handleArrayObjectUpdate('advices.medications_to_continue', index, 'dose', v)} />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <ValueRow label="Freq" value={item?.frequency} onChange={(v) => handleArrayObjectUpdate('advices.medications_to_continue', index, 'frequency', v)} />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <ValueRow label="Start" value={item?.start} onChange={(v) => handleArrayObjectUpdate('advices.medications_to_continue', index, 'start', v)} />
                          </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={6}>
                            <ValueRow label="Period" value={item?.period} onChange={(v) => handleArrayObjectUpdate('advices.medications_to_continue', index, 'period', v)} />
                          </Grid>
                          <Grid item xs={6} sm={6}>
                            <ValueRow label="Unit" value={item?.periodUnit} onChange={(v) => handleArrayObjectUpdate('advices.medications_to_continue', index, 'periodUnit', v)} />
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={() => handleMedicationAdd('advices.medications_to_continue')} sx={{ mt: 0.5 }}>
                      Add Medication
                    </Button>
                    {/* This list no longer auto-fills from the care plans —
                        continuing a drug at home is a decision, not a default. */}
                    <Button size="small" onClick={() => setCopyMedsOpen(true)} sx={{ mt: 0.5, ml: 1 }}>
                      Copy from administered…
                    </Button>

                    <Divider sx={{ my: 1 }} />
                    <ValueRow label="Diet Type" value={summaryData?.advices?.diet?.type} onChange={(v) => handleUpdate('advices.diet.type', v)} />
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Diet Recommendations</Typography>
                    <EditableStringList
                      items={summaryData?.advices?.diet?.recommendations}
                      itemLabel="Recommendation"
                      onItemChange={(i, v) => handleArrayUpdate("advices.diet.recommendations", i, v)}
                      onRemove={(i) => handleArrayRemove("advices.diet.recommendations", i)}
                      onAdd={() => handleArrayAdd("advices.diet.recommendations")}
                    />

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Activity Restrictions</Typography>
                    <EditableStringList
                      items={summaryData?.advices?.activity_restrictions}
                      itemLabel="Restriction"
                      onItemChange={(i, v) => handleArrayUpdate("advices.activity_restrictions", i, v)}
                      onRemove={(i) => handleArrayRemove("advices.activity_restrictions", i)}
                      onAdd={() => handleArrayAdd("advices.activity_restrictions")}
                    />

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Warning Signs</Typography>
                    <EditableStringList
                      items={summaryData?.advices?.warning_signs}
                      itemLabel="Warning Sign"
                      onItemChange={(i, v) => handleArrayUpdate("advices.warning_signs", i, v)}
                      onRemove={(i) => handleArrayRemove("advices.warning_signs", i)}
                      onAdd={() => handleArrayAdd("advices.warning_signs")}
                    />

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Follow-up</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Department" value={summaryData?.advices?.follow_up?.department} onChange={(v) => handleUpdate('advices.follow_up.department', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="After Days" kind="number" value={summaryData?.advices?.follow_up?.after_days} onChange={(v) => handleUpdate('advices.follow_up.after_days', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Purpose" value={summaryData?.advices?.follow_up?.purpose} onChange={(v) => handleUpdate('advices.follow_up.purpose', v)} />
                      </Grid>
                    </Grid>
                  </SectionCard>

                  {/* ── Referral Details ── */}
                  <SectionCard title="Referral Details">
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Referred By" value={summaryData?.referral_details?.referred_by} onChange={(v) => handleUpdate('referral_details.referred_by', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Reason for Referral" value={summaryData?.referral_details?.reason_for_referral} onChange={(v) => handleUpdate('referral_details.reason_for_referral', v)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <ValueRow label="Referral Date" value={summaryData?.referral_details?.referral_date} onChange={(v) => handleUpdate('referral_details.referral_date', v)} />
                      </Grid>
                    </Grid>
                  </SectionCard>

                  {/* ── Prepared By ── */}
                  <SectionCard title="Prepared By">
                    <Grid container spacing={1}>
                      {Object.keys(summaryData?.prepared_by || {}).map((key) => (
                        <Grid item xs={12} sm={4} key={`prepared-${key}`}>
                          <ValueRow label={toLabel(key)} value={summaryData?.prepared_by[key]} onChange={(v) => handleUpdate(`prepared_by.${key}`, v)} />
                        </Grid>
                      ))}
                    </Grid>
                  </SectionCard>

                  {/* ── Checked By ── */}
                  <SectionCard title="Checked By">
                    <Grid container spacing={1}>
                      {Object.keys(summaryData?.checked_by || {}).map((key) => (
                        <Grid item xs={12} sm={4} key={`checked-${key}`}>
                          <ValueRow label={toLabel(key)} value={summaryData?.checked_by[key]} onChange={(v) => handleUpdate(`checked_by.${key}`, v)} />
                        </Grid>
                      ))}
                    </Grid>
                  </SectionCard>
                  </Box>
                </Grid>

                {/* Preview Side */}
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    height: "100%",
                    overflowY: "auto",
                    p: 3,
                    backgroundColor: "#e0e0e0",
                  }}
                >
                  <DischargeSummaryPreview
                    data={
                      dischargeProcess?.outcome === "deceased" && summaryData
                        ? {
                            ...summaryData,
                            // Feeds the Cause of Death section in buildPdfDefinition
                            // (the preview). The finalized PDF comes from the edited
                            // HTML template, so for that path the cause must be in
                            // the template — see the Phase-2 spec's follow-up note.
                            _death: {
                              part1: dischargeProcess.mccd_part1 || [],
                              part2: dischargeProcess.mccd_part2 || [],
                              manner:
                                MANNER_OF_DEATH_LABELS[dischargeProcess.manner_of_death] ||
                                dischargeProcess.manner_of_death,
                              certifyingDoctorName: dischargeProcess.certifying_doctor_name,
                              timeOfDeath: dischargeProcess.death_datetime,
                            },
                          }
                        : summaryData
                    }
                  />
                </Grid>
              </Grid>
            )}
          </>
        )}

        {/* ─── STEP 2: Rich-Text Editor ─────────────────────────────── */}
        {currentStep === 2 && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden", 
              p: 1.5,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {resumeNotice && (
              <Alert severity="info" sx={{ mb: 1 }} onClose={() => setResumeNotice("")}>
                {resumeNotice}
              </Alert>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              {/* hydrateTemplate already marks every value it filled with
                  .auto-hydrated; nothing surfaced it until now. */}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showProvenance}
                    onChange={(e) => setShowProvenance(e.target.checked)}
                  />
                }
                label={<Typography variant="caption">Show auto-filled text</Typography>}
              />
              {lastSavedAt && (
                <Typography variant="caption" color="text.secondary">
                  Last saved: {lastSavedAt}
                </Typography>
              )}
            </Box>

            {isApprovedLocked && (
              <Alert severity="success" sx={{ mb: 1 }}>
                This summary is approved and locked. Reopen it for editing to make
                changes — the approval will be withdrawn.
              </Alert>
            )}

            <Box
              className={showProvenance ? "show-provenance" : undefined}
              sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
              <ClinicalEditor
                ref={editorRef}
                value={htmlContent}
                onChange={handleEditorChange}
                onBlur={handleEditorChange}
                readOnly={isApprovedLocked}
                height="100%"
                placeholder="Start typing clinical summary here..."
              />
            </Box>
          </Box>
        )}

        {/* ─── STEP 3: Preview & Submit ───────────────────────────── */}
        {currentStep === 3 && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 1.5, backgroundColor: "#fff", borderBottom: "1px solid #ddd", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
               <Typography variant="subtitle1" fontWeight={600} sx={{ ml: 1 }}>Final Preview</Typography>
               <FormControlLabel
                 control={
                   <Switch 
                     checked={printMode === "letterhead"}
                     onChange={(e) => setPrintMode(e.target.checked ? "letterhead" : "blank")}
                     color="primary"
                   />
                 }
                 label="Printing on pre-printed letterhead (omit the digital header & footer)"
                 sx={{ mr: 1 }}
               />
            </Box>

            {/* The letterhead is stamped onto the PDF by jsPDF after layout, so
                it cannot appear in this DOM preview. An earlier attempt to draw
                a lookalike above the page duplicated the template's own title
                and floated off the paper — a caption is honest, a fake is not. */}
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1, display: "block" }}>
              {printMode === "blank"
                ? "The hospital letterhead, page numbers and signature row are added to the PDF when you finalize."
                : "The letterhead and footer will be omitted — only page numbers and the signature row are added."}
            </Typography>

            {/* Advisory, not blocking: it names what is missing rather than
                guessing whether it matters. There was no completeness check
                anywhere — an empty summary finalized without objection. */}
            {(() => {
              const unmet = unmetCompletenessItems(summaryData);
              if (unmet.length === 0) return null;
              return (
                <Alert severity="warning" sx={{ m: 1.5, mb: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {unmet.length} item{unmet.length === 1 ? "" : "s"} still missing
                  </Typography>
                  <Typography variant="body2">
                    {unmet.map((i) => i.label).join(" · ")}
                  </Typography>
                  <Button size="small" sx={{ mt: 0.5 }} onClick={() => setCurrentStep(1)}>
                    Back to the clinical summary
                  </Button>
                </Alert>
              );
            })()}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                py: 4,
                backgroundColor: "#e0e0e0",
              }}
            >
              <PaginatedClinicalDocument html={(htmlContent || "") + marAnnexureHtml} />
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* ── Action bar ──────────────────────────────────────────────── */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #ddd" }}>
        {errorMessage && currentStep !== 1 && (
          <Typography color="error" variant="body2" sx={{ mr: "auto" }}>
            {errorMessage}
          </Typography>
        )}

        {/* Step 0: Close only */}
        {currentStep === 0 && (
          <Button
            onClick={handleClose}
            variant="contained"
            color="secondary"
          >
            Close
          </Button>
        )}

        {/* Step 1: Close + Compile */}
        {currentStep === 1 && (
          <>
            <Button
              onClick={handleClose}
              variant="contained"
              color="secondary"
              disabled={loading}
            >
              Close
            </Button>
            <Button
              onClick={handleCompileSummary}
              variant="contained"
              color="primary"
              disabled={loading || !summaryData}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : formLocked ? (
                "Rebuild Document from Form"
              ) : (
                // "Compile Clinical Summary" said nothing about running an AI
                // pass and producing a document, and the diff dialog makes the
                // old label actively wrong.
                "Generate Document"
              )}
            </Button>
          </>
        )}

        {/* Step 2: Save Draft + Proceed to Preview */}
        {currentStep === 2 && (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="secondary"
              disabled={savingDraft}
            >
              Close
            </Button>
            {isApprovedLocked ? (
              <Button
                onClick={handleReopenForEditing}
                variant="outlined"
                color="warning"
                disabled={reopening}
              >
                {reopening ? "Reopening…" : "Reopen for Editing"}
              </Button>
            ) : (
              <Button
                // Must not pass the click event through — handleSaveDraft's first
                // param is `silent`, and a truthy SyntheticEvent would suppress the
                // spinner and both toasts on the manual path.
                onClick={() => handleSaveDraft()}
                variant="outlined"
                color="primary"
                disabled={savingDraft}
                startIcon={
                  savingDraft ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {savingDraft ? "Saving…" : "Save Draft"}
              </Button>
            )}
            <Button
              onClick={handleProceedToPreview}
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
            >
              Preview & Submit
            </Button>
          </>
        )}

        {/* Step 3: Return to Editing + Finalize */}
        {currentStep === 3 && (
          <>
            <Button
              onClick={() => setCurrentStep(2)}
              disabled={submitting}
            >
              Return to Editing
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => setShowFinalizeConfirm(true)}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CheckCircleOutlineIcon />
                )
              }
            >
              {submitting
                ? "Finalizing…"
                : "Authenticate & Finalize (Print)"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>

      <Dialog open={showDraftPrompt} onClose={() => {}} fullWidth maxWidth="sm">
        <DialogTitle>Saved Draft Found</DialogTitle>
        <DialogContent>
          <Typography>
            A saved discharge draft exists for this patient. Would you like to resume
            editing it, or start a new discharge summary from scratch?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGenerateNew} color="secondary" variant="outlined">
            Start New
          </Button>
          <Button onClick={handleContinueWithDraft} color="primary" variant="contained">
            Resume Draft
          </Button>
        </DialogActions>
      </Dialog>

      <CopyMedicationsDialog
        open={copyMedsOpen}
        source={asArray(summaryData?.treatment_given?.medications_administered)}
        onClose={() => setCopyMedsOpen(false)}
        onConfirm={handleCopyMedications}
      />

      <ArticulationDiffDialog
        open={Boolean(pendingArticulation)}
        changes={pendingArticulation?.changes || []}
        onAccept={handleAcceptArticulation}
        onReject={handleRejectArticulation}
        busy={articulating}
      />

      <Dialog
        open={Boolean(pendingTemplate)}
        onClose={() => setPendingTemplate(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Discard your clinical summary edits?</DialogTitle>
        <DialogContent>
          <Typography>
            Changing the template regenerates the clinical summary from this
            admission's care plans. Everything you have edited on the form will be
            replaced.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingTemplate(null)} variant="outlined" color="secondary">
            Keep My Edits
          </Button>
          <Button
            onClick={() => {
              const { templateId, templateObj } = pendingTemplate;
              setPendingTemplate(null);
              applyTemplateSelection(templateId, templateObj);
            }}
            variant="contained"
            color="error"
          >
            Change Template
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showFinalizeConfirm}
        onClose={() => setShowFinalizeConfirm(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Finalize this discharge summary?</DialogTitle>
        <DialogContent>
          <Typography>
            This generates the PDF, uploads it as the patient's final discharge
            summary and opens it for printing. Re-finalizing later replaces it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowFinalizeConfirm(false)}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowFinalizeConfirm(false);
              handleFinalize();
            }}
            color="success"
            variant="contained"
          >
            Finalize &amp; Print
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showRecompileConfirm}
        onClose={() => setShowRecompileConfirm(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Discard document edits?</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedTemplateObj
              ? "You've edited this document in the rich-text editor. Rebuilding it from the clinical form will discard those edits. Rebuild from the form, or keep your edits and return to the editor?"
              : "The template this draft was built from isn't available, so rebuilding would silently produce a different layout. Keep your edits, or reselect a template from Step 1 first."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeepEditsAndContinue} color="secondary" variant="outlined">
            Keep Edits
          </Button>
          <Button
            onClick={handleConfirmRecompile}
            color="error"
            variant="contained"
            disabled={!selectedTemplateObj}
          >
            Rebuild from Form
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DischargeSummaryModal;
